import React, { useState, useRef, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../App.css';
import { PartNumberDetector } from '../utils/partNumberDetector';
import { MicIcon, MicIcon2, CameraIcon, InfoIcon } from '../components/Icons';
import { RotatingText } from '../components/RotatingText';
import { getApiKeyHeaders, getApiKeyHeadersForUpload } from '../utils/auth';
import { getBackendUrl, getGatewayUrl } from '../utils/api';
import MultiPartSelector from '../components/MultiPartSelector';

// Type definition for vehicle details form
type VehicleDetailsForm = {
  make: string;
  model: string;
  variant: string;
  fuelType: string;
  year: string;
  subAggregate: string;
  partNumber: string;
};

// Icons as React components
const PaperclipIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
  </svg>
);

const SendIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h8" />
  </svg>
);

const ImageIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClearIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const LoadingSpinner = () => (
  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);


// Helper: Build search URL with params (prevents state mismatch for concurrent users)
const buildSearchUrl = (params: {
  q: string;
  mode: 'text' | 'hybrid';
  imageIds?: string[];
  make?: string;
  model?: string;
  variant?: string;
  fuelType?: string;
  year?: string;
  subAggregate?: string;
  partNumber?: string;
}): string => {
  const urlParams = new URLSearchParams();
  if (params.q) urlParams.set('q', params.q);
  urlParams.set('mode', params.mode);
  if (params.imageIds && params.imageIds.length > 0) {
    urlParams.set('imageIds', params.imageIds.join(','));
  }
  // Pass vehicle details as explicit params so the backend can use them as overrides
  if (params.make) urlParams.set('make', params.make);
  if (params.model) urlParams.set('model', params.model);
  if (params.variant) urlParams.set('variant', params.variant);
  if (params.fuelType) urlParams.set('fuelType', params.fuelType);
  if (params.year) urlParams.set('year', params.year);
  if (params.subAggregate) urlParams.set('subAggregate', params.subAggregate);
  if (params.partNumber) urlParams.set('partNumber', params.partNumber);
  // correlation_id is now managed entirely by the backend via Redis + session_id
  return `/results?${urlParams.toString()}`;
};

function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine customer type from URL params (e.g., ?customerType=CV)
  const customerType = new URLSearchParams(location.search).get('customerType')?.toUpperCase() === 'CV' ? 'CV' : 'PV';

  const [searchText, setSearchText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedPreviews, setSelectedPreviews] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [inlineSuggestion, setInlineSuggestion] = useState<string>('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [partNumberIntent, setPartNumberIntent] = useState<{ partNumber: string; confidence: 'high' | 'medium' | 'low' } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const recordingStartRef = useRef<number | null>(null);
  const suggestionsAbortRef = useRef<AbortController | null>(null);
  const latestQueryRef = useRef<string>('');  // Track latest query to discard stale responses
  const requestSequenceRef = useRef<number>(0);  // Sequence number for race condition prevention

  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);

  
  // Vehicle database dropdown state (using mmvfy table)
  const [vehicleMakes, setVehicleMakes] = useState<string[]>([]);
  const [vehicleModels, setVehicleModels] = useState<string[]>([]);
  const [vehicleVariants, setVehicleVariants] = useState<string[]>([]);
  const [vehicleVariantsWithParts, setVehicleVariantsWithParts] = useState<Array<{
    partDescription: string | null;
    variant: string | null;
    fuelType: string | null;
    fromYear: number | null;
    toYear: number | null;
  }>>([]);
  const [vehicleFuelTypes, setVehicleFuelTypes] = useState<string[]>([]);
  const [vehicleYears, setVehicleYears] = useState<number[]>([]);
  const [loadingVehicleData, setLoadingVehicleData] = useState<boolean>(false);
  const [loadingYears, setLoadingYears] = useState<boolean>(false);
  
  // Sub aggregate autocomplete state
  const [subAggregateSuggestions, setSubAggregateSuggestions] = useState<string[]>([]);
  const [subAggregateInputValue, setSubAggregateInputValue] = useState<string>('');
  const [subAggregateInlineSuggestion, setSubAggregateInlineSuggestion] = useState<string>('');
  const [loadingSubAggregates, setLoadingSubAggregates] = useState<boolean>(false);
  const subAggregateInputRef = useRef<HTMLInputElement>(null);

  // Part number input state (separate from part name)
  const [partNumberInputValue, setPartNumberInputValue] = useState<string>('');

  // Multi-select parts for missing fields modal (separate from multipart search modal)
  const [modalSelectedParts, setModalSelectedParts] = useState<string[]>([]);

  // Missing fields modal state
  const [missingFieldsModal, setMissingFieldsModal] = useState<{
    isOpen: boolean;
    missingFields: string[];
    missingMMVFY: string[];
    missingSubAggregate: string[];
    normalizedQuery: any;
    source: 'tvs' | 'boodmo' | 'smart' | null;
    searchQuery: string;
    correlationId?: string;  // Track correlation_id from validation for linking log entries
  }>({
    isOpen: false,
    missingFields: [],
    missingMMVFY: [],
    missingSubAggregate: [],
    normalizedQuery: {},
    source: null,
    searchQuery: '',
    correlationId: undefined
  });
  
  // Vehicle number input state
  const [vehicleNumber, setVehicleNumber] = useState<string>('');
  const [isFetchingVehicleDetails, setIsFetchingVehicleDetails] = useState<boolean>(false);
  const [vehicleNumberError, setVehicleNumberError] = useState<string>('');
  const [yearError, setYearError] = useState<string>('');
  const [vehicleDetailsFromNumber, setVehicleDetailsFromNumber] = useState<boolean>(false); // Track if details were fetched from vehicle number
  const [isElectricVehicle, setIsElectricVehicle] = useState<boolean>(false); // Track if vehicle is electric
  const [vehicleNumberFromQuery, setVehicleNumberFromQuery] = useState<boolean>(false); // Track if vehicle number was auto-detected from search query
  const [vehicleClass, setVehicleClass] = useState<string>(''); // Track vehicle class from Vahan API
  
  const [vehicleDetails, setVehicleDetails] = useState<VehicleDetailsForm>({
    make: '',
    model: '',
    variant: '',
    fuelType: '',
    year: '',
    subAggregate: '',
    partNumber: ''
  });

  // Part detection modal state
  const [partDetectionModal, setPartDetectionModal] = useState<{
    isOpen: boolean;
    partName: string;
    partDescription: string;
    subAggregate: string;
    aggregate: string;
    imagePreview: string | null;
    isLoading: boolean;
  }>({
    isOpen: false,
    partName: '',
    partDescription: '',
    subAggregate: '',
    aggregate: '',
    imagePreview: null,
    isLoading: false
  });

  // Store imageIds from image upload (passed via URL params to results page)
  const [uploadedImageIds, setUploadedImageIds] = useState<string[]>([]);

  // Image detection loading and error state - PER IMAGE tracking
  // detectingImageIndices tracks which specific images are currently being detected
  const [detectingImageIndices, setDetectingImageIndices] = useState<Set<number>>(new Set());
  const [imageDetectionErrors, setImageDetectionErrors] = useState<Map<number, string>>(new Map());
  // Per-image detected parts (Map<imageIndex, partNames[]>)
  const [perImageParts, setPerImageParts] = useState<Map<number, string[]>>(new Map());

  // Computed: Check if ANY image is currently detecting (for MultiPartSelector spinner)
  const isDetectingParts = detectingImageIndices.size > 0;

  // Part validation error state (for custom parts in modal)
  const [partValidationError, setPartValidationError] = useState<string | null>(null);
  const [partValidationSuggestions, setPartValidationSuggestions] = useState<string[]>([]);

  // Rotating placeholder messages
  const placeholderMessages = [
    "Search by vehicle number, part number, name, image, or voice",
    "Try: TN10AB1234, 'brake pad', or upload a part photo",
    "Say: 'Clutch plate for 2016 i20 diesel'"
  ];

  useEffect(() => {
    try {
      const raw = localStorage.getItem('recent_searches');
      if (raw) setRecentSearches(JSON.parse(raw));
    } catch {}
  }, []);

  // Cleanup blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    // Keep a reference to current previews for cleanup
    const currentPreviews = selectedPreviews;

    return () => {
      // Revoke all blob URLs when component unmounts
      currentPreviews.forEach(url => {
        if (url && url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url);
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      });
    };
  }, [selectedPreviews]);

  // Handle redirect from ResultsPage with missing fields - show modal immediately
  useEffect(() => {
    const state = location.state as any;
    if (state?.redirectFromResults && state?.searchText && state?.missingFieldsError) {
      // Set the search text
      setSearchText(state.searchText);

      // Extract missing fields info from error
      const errorData = state.missingFieldsError?.detail || state.missingFieldsError;
      const missingMMVFY = errorData?.missingMMVFY || [];
      const missingSubAggregate = errorData?.missingSubAggregate || [];
      const normalizedQuery = errorData?.normalizedQuery || {};
      const correlationId = errorData?.correlationId || undefined;

      // Map backend field names to frontend field names
      const fieldMapping: Record<string, string> = {
        'Make': 'make',
        'Model': 'model',
        'Variant': 'variant',
        'Fuel Type': 'fuelType',
        'Year': 'year',
        'Sub Aggregate': 'subAggregate'
      };

      const mappedMissingMMVFY = missingMMVFY.map((field: string) => fieldMapping[field] || field.toLowerCase());
      const mappedMissingSubAggregate = missingSubAggregate.map((field: string) => fieldMapping[field] || field.toLowerCase());
      const allMappedMissingFields = [...mappedMissingMMVFY, ...mappedMissingSubAggregate];

      // Pre-fill vehicle details from normalized query
      setVehicleDetails(prev => ({
        ...prev,
        make: normalizedQuery.make || prev.make || '',
        model: normalizedQuery.model || prev.model || '',
        variant: normalizedQuery.variant || prev.variant || '',
        fuelType: normalizedQuery.fuelType || prev.fuelType || '',
        year: normalizedQuery.year ? String(normalizedQuery.year) : prev.year || '',
        subAggregate: normalizedQuery.subAggregate || normalizedQuery.partDescription || prev.subAggregate || ''
      }));

      // Pre-fill modalSelectedParts with detected parts
      const detectedParts: string[] = errorData?.detectedParts || [];
      if (detectedParts.length > 0) {
        console.log(`[Redirect] Using detected parts from multipart extraction: ${detectedParts.join(', ')}`);
        setModalSelectedParts(detectedParts);
        // Remove subAggregate from missing fields since we have detected parts
        const filteredMissingSubAgg: string[] = [];
        setMissingFieldsModal({
          isOpen: true,
          missingFields: [...mappedMissingMMVFY, ...filteredMissingSubAgg],
          missingMMVFY: mappedMissingMMVFY,
          missingSubAggregate: filteredMissingSubAgg,
          normalizedQuery: normalizedQuery,
          source: null,
          searchQuery: state.searchText,
          correlationId: correlationId,
        });
      } else if (normalizedQuery.subAggregate) {
        setModalSelectedParts([normalizedQuery.subAggregate]);
        // Show missing fields modal immediately with ALL missing fields
        setMissingFieldsModal({
          isOpen: true,
          missingFields: allMappedMissingFields,
          missingMMVFY: mappedMissingMMVFY,
          missingSubAggregate: mappedMissingSubAggregate,
          normalizedQuery: normalizedQuery,
          source: null,
          searchQuery: state.searchText,
          correlationId: correlationId,
        });
      } else {
        setModalSelectedParts([]);
        // Show missing fields modal immediately with ALL missing fields
        setMissingFieldsModal({
          isOpen: true,
          missingFields: allMappedMissingFields,
          missingMMVFY: mappedMissingMMVFY,
          missingSubAggregate: mappedMissingSubAggregate,
          normalizedQuery: normalizedQuery,
          source: null,
          searchQuery: state.searchText,
          correlationId: correlationId,
        });
      }

      // Fetch makes for dropdown when modal opens
      if (vehicleMakes.length === 0) {
        fetchVehicleMakes();
      }

      // Fetch sub aggregates for autocomplete
      fetchSubAggregates();

      // If make is pre-filled, fetch models
      if (normalizedQuery.make) {
        const skipVariantClear = Boolean(normalizedQuery.model);
        fetchVehicleModels(normalizedQuery.make, skipVariantClear);
      }

      // If both make and model are pre-filled, fetch variants, fuel types, and years
      if (normalizedQuery.make && normalizedQuery.model) {
        fetchVehicleVariants(normalizedQuery.make, normalizedQuery.model);
        fetchVehicleYears(
          normalizedQuery.make,
          normalizedQuery.model,
          normalizedQuery.variant || undefined,
          normalizedQuery.fuelType || undefined
        );
      }

      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);


  // Dropdown suggestions (debounced with race condition prevention)
  useEffect(() => {
    const query = searchText.trim();

    // Track latest query and increment request sequence for race condition prevention
    latestQueryRef.current = query;
    const thisRequestSeq = ++requestSequenceRef.current;

    // Immediately clear old suggestions when input changes to prevent stale data
    setSuggestions([]);
    setSuggestionsOpen(false);
    setInlineSuggestion('');

    if (!query) {
      setSelectedSuggestionIndex(-1);
      setPartNumberIntent(null);
      return;
    }

    try {
      const match = PartNumberDetector.detectPartNumber(query);
      setPartNumberIntent(match ? { partNumber: match.partNumber, confidence: match.confidence } : null);
    } catch {}

    // Adaptive debounce: longer wait for short queries (users likely still typing)
    const getDebounceTime = (queryLength: number) => {
      if (queryLength <= 2) return 350;  // Short queries: wait longer
      if (queryLength <= 4) return 280;  // Medium queries
      return 200;  // Longer queries: respond faster
    };

    const handler = setTimeout(async () => {
      let timeoutId: NodeJS.Timeout | null = null;
      const suggestionsBaseUrl = getBackendUrl();
      const currentQuery = query;  // Capture for this specific request

      try {
        // Abort any in-flight request
        if (suggestionsAbortRef.current) suggestionsAbortRef.current.abort();
        const ac = new AbortController();
        suggestionsAbortRef.current = ac;

        // Timeout for suggestions API (50 seconds for large vector searches)
        timeoutId = setTimeout(() => ac.abort(), 50000);

        const headers: HeadersInit = getApiKeyHeaders();

        const suggestionsUrl = `${suggestionsBaseUrl}/api/a9/suggestions?q=${encodeURIComponent(currentQuery)}&limit=10&customerType=${customerType}`;
        const resp = await fetch(suggestionsUrl, {
          signal: ac.signal,
          headers
        });

        if (timeoutId) clearTimeout(timeoutId);

        // RACE CONDITION CHECK 1: Verify this is still the latest request
        if (requestSequenceRef.current !== thisRequestSeq) {
          console.debug(`[Suggestions] Discarding stale response (seq ${thisRequestSeq}, current ${requestSequenceRef.current})`);
          return;
        }

        // RACE CONDITION CHECK 2: Verify query hasn't changed
        if (latestQueryRef.current !== currentQuery) {
          console.debug(`[Suggestions] Discarding stale response for "${currentQuery}", current query is "${latestQueryRef.current}"`);
          return;
        }

        const data = await resp.json();

        // RACE CONDITION CHECK 3: Validate backend returned the same query (if available)
        const responseQuery = data?.meta?.query;
        if (responseQuery && responseQuery.toLowerCase() !== currentQuery.toLowerCase()) {
          console.debug(`[Suggestions] Discarding mismatched response (sent "${currentQuery}", got "${responseQuery}")`);
          return;
        }

        // Final check after parsing - query might have changed during json parsing
        if (requestSequenceRef.current !== thisRequestSeq || latestQueryRef.current !== currentQuery) {
          return;
        }

        const suggestionsList = data?.data?.suggestions || [];
        const inlineSug = data?.data?.inlineSuggestion || '';

        // Set dropdown suggestions
        if (suggestionsList.length > 0) {
          setSuggestions(suggestionsList);
          setSuggestionsOpen(true);
          setSelectedSuggestionIndex(-1);
        } else {
          setSuggestions([]);
          setSuggestionsOpen(false);
          setSelectedSuggestionIndex(-1);
        }

        // Set inline suggestion
        if (inlineSug) {
          const normalizeSpaces = (str: string) => str.replace(/\s+/g, ' ').trim();
          const normalizedQuery = normalizeSpaces(currentQuery.toLowerCase());
          const normalizedSuggestion = normalizeSpaces(inlineSug.toLowerCase());

          if (normalizedSuggestion.startsWith(normalizedQuery) && normalizedSuggestion !== normalizedQuery) {
            setInlineSuggestion(inlineSug);
          } else {
            setInlineSuggestion('');
          }
        } else {
          setInlineSuggestion('');
        }
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);
        // Only log error if it's not an abort (user is still typing)
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Suggestions fetch error:', error);
          // Only clear if this is still the current request
          if (requestSequenceRef.current === thisRequestSeq) {
            setSuggestions([]);
            setSuggestionsOpen(false);
            setSelectedSuggestionIndex(-1);
            setInlineSuggestion('');
          }
        }
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }, getDebounceTime(query.length));

    return () => clearTimeout(handler);
  }, [searchText]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles: File[] = [];
    const errors: string[] = [];
    const newPreviews: string[] = [];

    // Calculate how many more files we can add (max 5 total)
    const remainingSlots = 5 - selectedFiles.length;
    if (remainingSlots <= 0) {
      setError('Maximum 5 images allowed.');
      return;
    }

    for (const file of files) {
      if (validFiles.length >= remainingSlots) {
        errors.push(`Only ${remainingSlots} more image(s) can be added.`);
        break;
      }
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type. Please use JPEG, PNG, or WebP.`);
        continue;
      }
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large. Maximum size is 10MB.`);
        continue;
      }
      if (file.size === 0) {
        errors.push(`${file.name}: File is empty.`);
        continue;
      }
      validFiles.push(file);
      try { newPreviews.push(URL.createObjectURL(file)); } catch {}
    }

    if (errors.length > 0) {
      setError(errors.join(' '));
    } else {
      setError('');
    }

    // Track starting index for new files (for merging detection results)
    const startingIndex = selectedFiles.length;

    // Add new files to existing files (not replace)
    const allFiles = [...selectedFiles, ...validFiles];
    setSelectedFiles(allFiles);
    setSelectedPreviews(prev => [...prev, ...newPreviews]);

    // Clear file input so same file can be selected again
    if (event.target) {
      event.target.value = '';
    }

    // Only process NEW files for part detection (not already detected ones)
    if (validFiles.length > 0) {
      await processNewImagesForPartDetection(validFiles, startingIndex);
    }
  };

  // Process NEW images for part detection (only processes new files, merges with existing results)
  const processNewImagesForPartDetection = async (newFiles: File[], startingIndex: number) => {
    // Calculate indices for new images being processed
    const newImageIndices = newFiles.map((_, i) => startingIndex + i);

    // Add new image indices to detecting set (per-image tracking)
    setDetectingImageIndices(prev => {
      const updated = new Set(prev);
      newImageIndices.forEach(idx => updated.add(idx));
      return updated;
    });

    // Keep existing perImageParts and errors, we'll merge new ones

    try {
      // Image search: Show Add Info modal directly with loading state (only if not already open)
      const missingMMVFY = ['Make', 'Model', 'Variant', 'Fuel Type', 'Year'];
      const fieldMapping: Record<string, string> = {
        'Make': 'make',
        'Model': 'model',
        'Variant': 'variant',
        'Fuel Type': 'fuelType',
        'Year': 'year',
        'Sub Aggregate': 'subAggregate'
      };
      const mappedMissingMMVFY = missingMMVFY.map(field => fieldMapping[field] || field.toLowerCase());

      // Show Add Info modal immediately - Part Name field shows loading indicator
      if (!missingFieldsModal.isOpen) {
        setMissingFieldsModal({
          isOpen: true,
          missingFields: [...mappedMissingMMVFY, 'subAggregate'],
          missingMMVFY: mappedMissingMMVFY,
          missingSubAggregate: ['subAggregate'],
          normalizedQuery: {
            subAggregate: null,
            partDescription: null,
            original_query: ''
          },
          source: null,
          searchQuery: ''
        });
      }

      // Fetch makes for dropdown when modal opens
      if (vehicleMakes.length === 0) {
        fetchVehicleMakes();
      }

      // Fetch sub aggregates for autocomplete
      fetchSubAggregates();

      // Process only NEW images - upload only new files
      const formData = new FormData();
      newFiles.forEach(file => {
        formData.append('files', file);
      });

      const gatewayUrl = getGatewayUrl();
      const headers: HeadersInit = getApiKeyHeadersForUpload();
      const response = await fetch(`${gatewayUrl}/api/upload/image`, {
        method: 'POST',
        body: formData,
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to process images');
      }

      const data = await response.json();

      // Use perImageStatus from backend for reliable 1:1 mapping with files
      const perImageStatus = data.data?.perImageStatus || [];
      const newDetectedParts: string[] = [];
      const newErrors = new Map<number, string>();
      const newImagePartsMap = new Map<number, string[]>();

      // Process perImageStatus array (1:1 with uploaded NEW files)
      // Adjust indices by startingIndex to match the correct file position
      perImageStatus.forEach((status: any) => {
        const backendIndex = status.index;
        const actualIndex = startingIndex + backendIndex; // Adjust index for merged file list

        if (status.status === 'error') {
          newErrors.set(actualIndex, status.error || 'Detection failed');
        } else if (status.status === 'success' && status.partType) {
          const normalizedPart = status.partType.trim().toUpperCase();
          newDetectedParts.push(normalizedPart);
          newImagePartsMap.set(actualIndex, [normalizedPart]);
        } else if (status.status === 'no_parts_detected') {
          // No error, just no parts found - leave imagePartsMap empty for this index
          newImagePartsMap.set(actualIndex, []);
        }
      });

      // Fallback to analysis array if perImageStatus not available
      if (perImageStatus.length === 0) {
        const analysisArray = data.data?.analysis || [];
        if (Array.isArray(analysisArray)) {
          analysisArray.forEach((analysisItem: any, backendIndex: number) => {
            const actualIndex = startingIndex + backendIndex;
            if (analysisItem?.error) {
              newErrors.set(actualIndex, analysisItem.error);
              return;
            }
            const analysis = Array.isArray(analysisItem) ? analysisItem : [analysisItem];
            if (analysis.length > 0) {
              const firstResult = analysis[0];
              const partName = firstResult?.partType || firstResult?.partDescription || firstResult?.description || '';
              if (partName && partName.trim()) {
                const normalizedPart = partName.trim().toUpperCase();
                newDetectedParts.push(normalizedPart);
                newImagePartsMap.set(actualIndex, [normalizedPart]);
              }
            }
          });
        }
      }

      // MERGE new results with existing ones (don't replace)
      setPerImageParts(prev => {
        const merged = new Map(prev);
        newImagePartsMap.forEach((parts, index) => merged.set(index, parts));
        return merged;
      });
      setImageDetectionErrors(prev => {
        const merged = new Map(prev);
        newErrors.forEach((error, index) => merged.set(index, error));
        return merged;
      });

      // Combine existing parts with new detected parts, then deduplicate
      const allDetectedParts = [...modalSelectedParts, ...newDetectedParts];
      const uniqueParts = Array.from(new Set(allDetectedParts));

      // Store imageIds for this upload (merge with existing)
      const newImageIds = data.data?.imageIds || [];
      setUploadedImageIds(prev => [...prev, ...newImageIds.filter((id: string | null) => id !== null)]);

      // Update modalSelectedParts with all unique detected parts
      if (uniqueParts.length > 0) {
        setModalSelectedParts(uniqueParts);
        // Set first part as subAggregate for backward compatibility (only if not already set)
        setVehicleDetails(prev => ({
          ...prev,
          subAggregate: prev.subAggregate || uniqueParts[0]
        }));
        if (!subAggregateInputValue) {
          setSubAggregateInputValue(uniqueParts[0]);
        }
      }

      // Update the modal with detected parts info - always keep Part Info section visible for editing
      const missingSubAgg = ['subAggregate']; // Always show Part Information section
      const allMissingFields = [...mappedMissingMMVFY, ...missingSubAgg];
      const partsDisplay = uniqueParts.length > 0 ? uniqueParts.join(', ') : '';

      setMissingFieldsModal(prev => ({
        ...prev,
        missingFields: allMissingFields,
        missingSubAggregate: missingSubAgg,
        normalizedQuery: {
          subAggregate: uniqueParts.length > 0 ? uniqueParts[0] : null,
          partDescription: partsDisplay,
          original_query: partsDisplay,
          detectedParts: uniqueParts // Store all detected parts
        },
        searchQuery: partsDisplay
      }));

      // Clear detecting state for these specific images AFTER all state updates
      // Use requestAnimationFrame to ensure React has rendered the updates
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setDetectingImageIndices(prev => {
            const updated = new Set(prev);
            newImageIndices.forEach(idx => updated.delete(idx));
            return updated;
          });
        });
      });

    } catch (error) {
      console.error('Error processing images for part detection:', error);
      // Keep modal open for manual entry - Part Name field will be editable
      // Set errors only for the new images being processed
      setImageDetectionErrors(prev => {
        const merged = new Map(prev);
        newImageIndices.forEach(idx => merged.set(idx, 'Detection failed'));
        return merged;
      });
      // Clear detecting state for these specific images on error
      setDetectingImageIndices(prev => {
        const updated = new Set(prev);
        newImageIndices.forEach(idx => updated.delete(idx));
        return updated;
      });
    }
    // Note: Don't use finally block - we clear loading state after parts are set in the try block
  };


  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dtFiles = Array.from(e.dataTransfer.files || []);
    if (dtFiles.length === 0) return;
    const input = { target: { files: dtFiles } } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleFileChange(input);
  };

  // Vehicle data API functions (using mmvfy table)
  const fetchVehicleMakes = async (): Promise<void> => {
    try {
      setLoadingVehicleData(true);
      const { getMmvfyMakes } = await import('../utils/mmvfyApi');
      const makes = await getMmvfyMakes();
      setVehicleMakes(makes);

      // Fix case mismatch: If vehicleDetails.make is set but doesn't match any make exactly,
      // find case-insensitive match and update to the exact case from dropdown options
      setVehicleDetails(prev => {
        if (prev.make && makes.length > 0) {
          const exactMatch = makes.find(m => m === prev.make);
          if (!exactMatch) {
            // Try case-insensitive match
            const caseInsensitiveMatch = makes.find(
              m => m.toLowerCase() === prev.make.toLowerCase()
            );
            if (caseInsensitiveMatch) {
              console.log(`[fetchVehicleMakes] Case mismatch fixed: "${prev.make}" -> "${caseInsensitiveMatch}"`);
              return { ...prev, make: caseInsensitiveMatch };
            }
          }
        }
        return prev;
      });
    } catch (error) {
      console.error('Error fetching vehicle makes from mmvfy table:', error);
      setError('Failed to load vehicle makes. Please try again.');
    } finally {
      setLoadingVehicleData(false);
    }
  };
  
  const fetchVehicleModels = async (make: string, skipVariantClear: boolean = false): Promise<void> => {
    if (!make || !make.trim()) {
      setVehicleModels([]);
      setVehicleVariants([]);
      setVehicleVariantsWithParts([]);
      setVehicleFuelTypes([]);
      return;
    }

    try {
      console.log(`[fetchVehicleModels] Starting fetch for make="${make}", skipVariantClear=${skipVariantClear}`);
      setLoadingVehicleData(true);
      const { getMmvfyModels } = await import('../utils/mmvfyApi');
      const models = await getMmvfyModels(make);
      console.log(`[fetchVehicleModels] Received ${models.length} models for make="${make}"`);
      setVehicleModels(models);

      // Fix case mismatch: If vehicleDetails.model is set but doesn't match any model exactly,
      // find case-insensitive match and update to the exact case from dropdown options
      setVehicleDetails(prev => {
        if (prev.model && models.length > 0) {
          const exactMatch = models.find(m => m === prev.model);
          if (!exactMatch) {
            // Try case-insensitive match
            const caseInsensitiveMatch = models.find(
              m => m.toLowerCase() === prev.model.toLowerCase()
            );
            if (caseInsensitiveMatch) {
              console.log(`[fetchVehicleModels] Case mismatch fixed: "${prev.model}" -> "${caseInsensitiveMatch}"`);
              return { ...prev, model: caseInsensitiveMatch };
            }
          }
        }
        return prev;
      });

      // Clear variants and fuel types when make changes - unless skipVariantClear is true
      // (skipVariantClear is used when model is already pre-filled and variants are being fetched in parallel)
      if (!skipVariantClear) {
        setVehicleVariants([]);
        setVehicleVariantsWithParts([]);
        setVehicleFuelTypes([]);
      }
    } catch (error) {
      console.error(`[fetchVehicleModels] Error fetching vehicle models from mmvfy table for make="${make}":`, error);
      setVehicleModels([]);
    } finally {
      setLoadingVehicleData(false);
    }
  };
  
  // ⚡ OPTIMIZED: Fetch variants and fuel types in parallel using FAST endpoints with caching
  const fetchVehicleVariantsAndFuelTypes = async (make: string, model: string): Promise<void> => {
    if (!make || !make.trim() || !model || !model.trim()) {
      console.log(`[fetchVehicleVariantsAndFuelTypes] Skipping - make: "${make}", model: "${model}"`);
      setVehicleVariants([]);
      setVehicleVariantsWithParts([]);
      setVehicleFuelTypes([]);
      return;
    }
    
    try {
      console.log(`[fetchVehicleVariantsAndFuelTypes] ⚡ Starting PARALLEL fetch for make="${make}", model="${model}"`);
      setLoadingVehicleData(true);
      
      // Import fast endpoints and cache utility
      const { getMmvfyVariantsFast, getMmvfyFuelTypesFast } = await import('../utils/mmvfyApi');
      const { cachedApiCall } = await import('../utils/apiCache');
      
      // ⚡ PARALLEL FETCH: Fetch both variants and fuel types simultaneously with caching
      const [variants, fuelTypes] = await Promise.all([
        cachedApiCall(
          'vehicle-variants-fast',
          { make: make.trim(), model: model.trim() },
          () => getMmvfyVariantsFast(make.trim(), model.trim()),
          5 * 60 * 1000 // 5 minutes cache
        ),
        cachedApiCall(
          'vehicle-fuel-types-fast',
          { make: make.trim(), model: model.trim() },
          () => getMmvfyFuelTypesFast(make.trim(), model.trim()),
          5 * 60 * 1000 // 5 minutes cache
        )
      ]);
      
      console.log(`[fetchVehicleVariantsAndFuelTypes] ⚡ Successfully fetched ${variants.length} variants and ${fuelTypes.length} fuel types for ${make} ${model}`);
      setVehicleVariants(variants);
      setVehicleFuelTypes(fuelTypes);

      // Fix case mismatch for variant and fuelType
      setVehicleDetails(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        // Fix variant case mismatch
        if (prev.variant && variants.length > 0) {
          const exactMatch = variants.find(v => v === prev.variant);
          if (!exactMatch) {
            const caseInsensitiveMatch = variants.find(
              v => v.toLowerCase() === prev.variant.toLowerCase()
            );
            if (caseInsensitiveMatch) {
              console.log(`[fetchVehicleVariantsAndFuelTypes] Variant case mismatch fixed: "${prev.variant}" -> "${caseInsensitiveMatch}"`);
              updated.variant = caseInsensitiveMatch;
              hasChanges = true;
            }
          }
        }

        // Fix fuelType case mismatch
        if (prev.fuelType && fuelTypes.length > 0) {
          const exactMatch = fuelTypes.find(f => f === prev.fuelType);
          if (!exactMatch) {
            const caseInsensitiveMatch = fuelTypes.find(
              f => f.toLowerCase() === prev.fuelType.toLowerCase()
            );
            if (caseInsensitiveMatch) {
              console.log(`[fetchVehicleVariantsAndFuelTypes] FuelType case mismatch fixed: "${prev.fuelType}" -> "${caseInsensitiveMatch}"`);
              updated.fuelType = caseInsensitiveMatch;
              hasChanges = true;
            }
          }
        }

        return hasChanges ? updated : prev;
      });
    } catch (error) {
      console.error(`[fetchVehicleVariantsAndFuelTypes] Error fetching data for ${make} ${model}:`, error);
      setVehicleVariants([]);
      setVehicleFuelTypes([]);
    } finally {
      setLoadingVehicleData(false);
    }
  };
  
  // Keep original functions for backward compatibility (but they now call the optimized version)
  const fetchVehicleVariants = async (make: string, model: string): Promise<void> => {
    await fetchVehicleVariantsAndFuelTypes(make, model);
  };
  
  const fetchVehicleFuelTypes = async (make: string, model: string): Promise<void> => {
    // This is now handled by fetchVehicleVariantsAndFuelTypes
    // Keep empty to maintain compatibility
  };

  // Fetch years from unified lookup API based on current vehicle context
  const fetchVehicleYears = async (
    make: string,
    model: string,
    variant?: string,
    fuelType?: string
  ): Promise<void> => {
    if (!make || !make.trim() || !model || !model.trim()) {
      setVehicleYears([]);
      return;
    }

    try {
      setLoadingYears(true);
      const { getMmvfyYears } = await import('../utils/mmvfyApi');
      const { cachedApiCall } = await import('../utils/apiCache');

      const cacheParams: Record<string, string> = {
        make: make.trim(),
        model: model.trim(),
      };
      if (variant?.trim()) cacheParams.variant = variant.trim();
      if (fuelType?.trim()) cacheParams.fuelType = fuelType.trim();

      const years = await cachedApiCall(
        'vehicle-years-lookup',
        cacheParams,
        () => getMmvfyYears(make.trim(), model.trim(), variant?.trim(), fuelType?.trim()),
        5 * 60 * 1000 // 5 minutes cache
      );

      console.log(`[fetchVehicleYears] Got ${years.length} years for ${make} ${model} ${variant || ''} ${fuelType || ''}`);

      // Only update years if filtered query returned results
      // If filtered query (with variant/fuelType) returns 0, keep existing make+model years
      if (years.length > 0) {
        setVehicleYears(years);
      } else {
        setVehicleYears(prev => {
          if (prev.length > 0) {
            console.log(`[fetchVehicleYears] Filtered query returned 0 years, keeping existing ${prev.length} years from broader query`);
            return prev;
          }
          return years;
        });
      }

      // Auto-select year if pre-filled value matches or fix case
      setVehicleDetails(prev => {
        if (prev.year && years.length > 0) {
          const yearNum = Number(prev.year);
          if (yearNum && !years.includes(yearNum)) {
            // Pre-filled year not in available list - clear it
            console.log(`[fetchVehicleYears] Pre-filled year ${prev.year} not in available years, clearing`);
            return { ...prev, year: '' };
          }
        }
        return prev;
      });
    } catch (error) {
      console.error(`[fetchVehicleYears] Error fetching years:`, error);
      // On error, keep existing years if available
      setVehicleYears(prev => prev.length > 0 ? prev : []);
    } finally {
      setLoadingYears(false);
    }
  };

  const fetchSubAggregates = async (searchQuery: string = ''): Promise<void> => {
    try {
      setLoadingSubAggregates(true);
      const backendUrl = getBackendUrl();
      const headers: HeadersInit = getApiKeyHeaders();

      const url = searchQuery.trim()
        ? `${backendUrl}/api/search/vehicles/sub-aggregates?q=${encodeURIComponent(searchQuery.trim())}`
        : `${backendUrl}/api/search/vehicles/sub-aggregates`;
      
      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch sub aggregates');
      }
      
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setSubAggregateSuggestions(data.data);
        
        // Find best matching inline suggestion
        if (searchQuery.trim() && data.data.length > 0) {
          const queryLower = searchQuery.trim().toLowerCase();
          // Find first suggestion that starts with the query
          const bestMatch = data.data.find((suggestion: string) => 
            suggestion.toLowerCase().startsWith(queryLower)
          );
          if (bestMatch && bestMatch.toLowerCase() !== queryLower) {
            setSubAggregateInlineSuggestion(bestMatch);
          } else {
            setSubAggregateInlineSuggestion('');
          }
        } else {
          setSubAggregateInlineSuggestion('');
        }
      } else {
        setSubAggregateSuggestions([]);
        setSubAggregateInlineSuggestion('');
      }
    } catch (error) {
      console.error('Error fetching sub aggregates:', error);
      setSubAggregateSuggestions([]);
      setSubAggregateInlineSuggestion('');
    } finally {
      setLoadingSubAggregates(false);
    }
  };
  
  // Helper function to check if a value contains vehicle details
  const containsVehicleDetails = (value: string, normalizedQuery: any): boolean => {
    if (!value || !normalizedQuery) return false;
    
    const valueUpper = value.toUpperCase();
    const make = normalizedQuery.make?.toUpperCase() || '';
    const model = normalizedQuery.model?.toUpperCase() || '';
    const year = normalizedQuery.year ? String(normalizedQuery.year) : '';
    
    // Check if value contains make, model, or year
    return (
      (make && valueUpper.includes(make)) ||
      (model && valueUpper.includes(model)) ||
      (year && valueUpper.includes(year))
    );
  };
  
  // Helper function to remove vehicle number from text
  const removeVehicleNumberFromText = (text: string): string => {
    if (!text) return text;
    
    // Detect vehicle number in the text
    const vehicleNumber = PartNumberDetector.detectVehicleNumber(text);
    if (vehicleNumber) {
      // Create patterns to match vehicle number in various formats
      // Standard format: AA00AA0000 or AA 00 AA 0000 or AA-00-AA-0000
      // Extract parts from normalized vehicle number
      const match = vehicleNumber.match(/^([A-Z]{2})([0-9]{2})([A-Z]{1,2})([0-9]{4})$/);
      if (match) {
        const [, state, district, series, number] = match;
        
        // Create multiple patterns to match various formats
        const patterns = [
          // Exact match (normalized)
          new RegExp(vehicleNumber, 'gi'),
          // With spaces: AA 00 AA 0000
          new RegExp(`${state}\\s+${district}\\s+${series}\\s+${number}`, 'gi'),
          // With hyphens: AA-00-AA-0000
          new RegExp(`${state}-${district}-${series}-${number}`, 'gi'),
          // With mixed separators
          new RegExp(`${state}[\\s-]+${district}[\\s-]+${series}[\\s-]+${number}`, 'gi'),
          // Without separators but with word boundaries
          new RegExp(`\\b${state}${district}${series}${number}\\b`, 'gi'),
        ];
        
        let cleaned = text;
        for (const pattern of patterns) {
          cleaned = cleaned.replace(pattern, '').trim();
        }
        
        // Clean up extra spaces and punctuation
        cleaned = cleaned.replace(/\s+/g, ' ').trim();
        // Remove leading/trailing punctuation that might be left
        cleaned = cleaned.replace(/^[,\s-]+|[,\s-]+$/g, '').trim();
        
        return cleaned;
      }
    }
    
    // Also check for Bharat series format: 22BH1234AB
    const bharatMatch = text.match(/\b([0-9]{2}BH[0-9]{4}[A-Z]{1,2})\b/gi);
    if (bharatMatch) {
      let cleaned = text;
      for (const match of bharatMatch) {
        cleaned = cleaned.replace(new RegExp(match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '').trim();
      }
      cleaned = cleaned.replace(/\s+/g, ' ').trim();
      return cleaned;
    }
    
    return text;
  };
  
  // Helper function to extract variant-like patterns from query when variant is missing
  const extractVariantFromQuery = (query: string, make: string, model: string): string => {
    if (!query || !make || !model) return '';
    
    const queryUpper = query.toUpperCase();
    const makeUpper = make.toUpperCase();
    const modelUpper = model.toUpperCase();
    
    // Find the position after make and model in the query
    const makeIndex = queryUpper.indexOf(makeUpper);
    const modelIndex = queryUpper.indexOf(modelUpper, makeIndex >= 0 ? makeIndex + makeUpper.length : 0);
    
    if (modelIndex < 0) return '';
    
    // Extract text after model (this likely contains variant info)
    let afterModel = query.substring(modelIndex + modelUpper.length).trim();
    
    console.log(`[Variant Extraction] Text after model "${model}": "${afterModel}"`);
    
    // Remove fuel type and year if present
    const fuelTypes = ['PETROL', 'DIESEL', 'CNG', 'ELECTRIC', 'HYBRID'];
    const yearPattern = /\b(19|20)\d{2}\b/;
    
    // Remove fuel type
    for (const fuel of fuelTypes) {
      const fuelRegex = new RegExp(`\\b${fuel}\\b`, 'gi');
      afterModel = afterModel.replace(fuelRegex, '').trim();
    }
    
    // Remove year
    afterModel = afterModel.replace(yearPattern, '').trim();
    
    console.log(`[Variant Extraction] After removing fuel/year: "${afterModel}"`);
    
    // Remove common part names that might appear (like AIR FILTER, BRAKE PAD, etc.)
    // But keep variant patterns like ELITE, SPORTZ, 2ND GEN F/L, 1.2L, etc.
    const partNamePatterns = [
      'AIR FILTER', 'BRAKE PAD', 'OIL FILTER', 'SPARK PLUG', 'HEADLIGHT',
      'CLUTCH', 'GEAR', 'ENGINE', 'TRANSMISSION', 'RADIATOR'
    ];
    
    for (const partName of partNamePatterns) {
      const partRegex = new RegExp(`\\b${partName}\\b`, 'gi');
      afterModel = afterModel.replace(partRegex, '').trim();
    }
    
    console.log(`[Variant Extraction] After removing part names: "${afterModel}"`);
    
    // Clean up extra spaces
    afterModel = afterModel.replace(/\s+/g, ' ').trim();
    
    // If we have meaningful content left (more than 2 chars), return it as variant
    if (afterModel.length > 2) {
      console.log(`[Variant Extraction] Final extracted variant: "${afterModel}"`);
      return afterModel;
    }
    
    console.log(`[Variant Extraction] No variant extracted (length: ${afterModel.length})`);
    return '';
  };
  
  // Helper function to remove variant-like patterns from part name
  // Removes patterns like "2ND GEN F/L 1.2L SPORTZ" from part names
  // This ensures only the actual part name (e.g., "AIR FILTER") is shown in the modal
  const removeVariantPatternsFromPartName = (text: string, normalizedQuery: any): string => {
    if (!text) return text;
    
    let cleaned = text.trim();
    
    // Get variant from normalized query if available
    const variant = normalizedQuery?.variant?.toUpperCase()?.trim() || '';
    
    // Step 1: Remove the variant if it's present in the text
    // This handles cases where variant is "2ND GEN F/L 1.2L SPORTZ" or just "SPORTZ"
    if (variant) {
      // Split variant into words and remove each word from the text
      const variantWords = variant.split(/\s+/).filter((w: string) => w.length > 0);
      for (const word of variantWords) {
        // Use word boundaries to match whole words only
        const wordPattern = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        cleaned = cleaned.replace(wordPattern, '').trim();
      }
    }
    
    // Step 2: Remove common variant patterns that might not be in the variant field:
    // - Generation patterns: "1ST GEN", "2ND GEN", "3RD GEN", "4TH GEN", "5TH GEN", etc.
    // - Facelift patterns: "F/L", "FL", "FACELIFT"
    // - Engine size patterns: "1.2L", "1.5L", "2.0L", etc. (with optional decimal)
    
    // Generation patterns (1ST, 2ND, 3RD, 4TH, 5TH, etc. + GEN)
    cleaned = cleaned.replace(/\b([1-9](?:ST|ND|RD|TH))\s+GEN(?:ERATION)?\b/gi, '').trim();
    
    // Facelift patterns
    cleaned = cleaned.replace(/\bF\/L\b/gi, '').trim();
    cleaned = cleaned.replace(/\bF\s*L\b/gi, '').trim();
    cleaned = cleaned.replace(/\bFACELIFT\b/gi, '').trim();
    
    // Engine size patterns (e.g., "1.2L", "1.5L", "2.0L", "1.2 L", "1.5 L")
    // Match patterns like: 1.2L, 1.5L, 2.0L, 1.2 L, 1.5 L, etc.
    cleaned = cleaned.replace(/\b\d+\.?\d*\s*L\b/gi, '').trim();
    
    // Step 3: Remove common variant name patterns only if they appear alongside other variant indicators
    // This prevents removing actual part names that might contain variant-like words
    // Only remove variant names if the text contains other variant indicators (GEN, F/L, engine size)
    const hasVariantIndicators = /\b(?:[1-9](?:ST|ND|RD|TH)\s+GEN|F\/L|F\s*L|FACELIFT|\d+\.?\d*\s*L)\b/gi.test(cleaned);
    
    if (hasVariantIndicators || variant) {
      // Only remove variant names if we've already detected variant indicators or if variant is set
      // This is safer - we only remove variant names when we're confident they're part of the variant
      const commonVariantNames = [
        'ELITE', 'SPORTZ', 'TREND', 'ZXI', 'VDI', 'VDI+', 'VXI', 'LXI', 'ZXI+',
        'VX', 'VX+', 'ZX', 'ZX+', 'EX', 'EX+', 'SX', 'SX+', 'LX', 'LX+',
        'BASE', 'MID', 'TOP', 'PREMIUM', 'DELUXE', 'STANDARD', 'MAGNA', 'AURA'
      ];
      
      for (const variantName of commonVariantNames) {
        // Use word boundaries to match whole words only
        const variantNamePattern = new RegExp(`\\b${variantName}\\b`, 'gi');
        // Only remove if the text is longer than just the variant name (to avoid removing actual part names)
        if (cleaned.length > variantName.length) {
          cleaned = cleaned.replace(variantNamePattern, '').trim();
        }
      }
    }
    
    // Clean up extra spaces and punctuation
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    cleaned = cleaned.replace(/^[,\s-]+|[,\s-]+$/g, '').trim();
    
    // If after cleaning we have nothing meaningful left, return the original text
    // This prevents removing everything and leaving an empty string
    if (!cleaned || cleaned.length < 2) {
      return text.trim();
    }
    
    return cleaned;
  };
  
  // Helper function to get valid sub aggregate detected value
  const getValidSubAggregateDetectedValue = (normalizedQuery: any): string | null => {
    // First check subAggregate
    if (normalizedQuery?.subAggregate) {
      let subAgg = normalizedQuery.subAggregate;
      // Remove vehicle number if present
      subAgg = removeVehicleNumberFromText(subAgg);
      // Remove variant patterns
      subAgg = removeVariantPatternsFromPartName(subAgg, normalizedQuery);
      // Check if it still contains vehicle details (make, model, year)
      if (subAgg && !containsVehicleDetails(subAgg, normalizedQuery)) {
        return subAgg.trim();
      }
    }
    
    // Then check partDescription, but filter out vehicle details
    if (normalizedQuery?.partDescription) {
      let partDesc = normalizedQuery.partDescription;
      // Remove vehicle number if present
      partDesc = removeVehicleNumberFromText(partDesc);
      // Remove variant patterns
      partDesc = removeVariantPatternsFromPartName(partDesc, normalizedQuery);
      if (partDesc && !containsVehicleDetails(partDesc, normalizedQuery)) {
        return partDesc.trim();
      }
    }
    
    return null;
  };

  const toggleRecording = async () => {
    try {
      if (!isRecording) {
        setError('');
        // Check if browser supports mediaDevices
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setError('Voice search is not supported in your browser. Please use a modern browser with microphone support.');
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mr;
        audioChunksRef.current = [];
        try {
          const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);
          audioContextRef.current = audioCtx;
          analyserRef.current = analyser;
          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            analyser.getByteTimeDomainData(dataArray);
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
              const v = (dataArray[i] - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / dataArray.length);
            setAudioLevel(Math.min(1, rms * 4));
            if (recordingStartRef.current) {
              setRecordingMs(Date.now() - recordingStartRef.current);
            }
            rafIdRef.current = requestAnimationFrame(tick);
          };
          rafIdRef.current = requestAnimationFrame(tick);
        } catch {}
        recordingStartRef.current = Date.now();
        mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        mr.onstop = async () => {
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const form = new FormData();
          form.append('file', blob, 'audio.webm');
          try {
            const headers: HeadersInit = getApiKeyHeadersForUpload();
            const resp = await fetch(`${getBackendUrl()}/api/asr?mode=translate`, {
              method: 'POST',
              body: form,
              headers
            });
            const data = await resp.json();
            // Check if transcription text exists and is valid
            const transcribedText = data?.text?.trim() || '';
            
            // Check for silence indicators or default YouTube-like messages
            const isSilence = !transcribedText || 
              transcribedText.toLowerCase().includes('No Speech found');
            
            if (resp.ok && transcribedText && !isSilence) {
              // Only populate the search input field, don't auto-search
              // User needs to manually click send button or upload images and click send
              setSearchText(transcribedText);
              // Focus the textarea so user can review and edit if needed
              try {
                if (textareaRef.current) {
                  textareaRef.current.focus();
                  // Set cursor to end of text
                  setTimeout(() => {
                    try {
                      if (textareaRef.current) {
                        textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
                      }
                    } catch (err) {
                      // Ignore selection errors
                    }
                  }, 0);
                }
              } catch (err) {
                // Ignore focus errors
              }
            } else {
              // Check if it's silence/no speech detected
              const errorMsg = typeof data?.error === 'string'
                ? data.error
                : (data?.error?.message || data?.message || data?.detail || '');
              
              // If backend returned an error status, show its message (don't incorrectly label it as silence).
              if (!resp.ok) {
                if (errorMsg) {
                  setError(errorMsg);
                } else {
                  setError('Transcription failed. Please try recording again.');
                }
              } else {
                // Successful response but empty/invalid transcript => treat as silence.
                if (
                  isSilence ||
                  errorMsg.toLowerCase().includes('silence') ||
                  errorMsg.toLowerCase().includes('no speech') ||
                  errorMsg.toLowerCase().includes('empty') ||
                  errorMsg.toLowerCase().includes('no audio')
                ) {
                  setError('No speech detected. Please click the microphone and record your voice to search.');
                } else if (errorMsg) {
                  setError(errorMsg);
                } else {
                  setError('Transcription failed. Please try recording again.');
                }
              }
            }
          } catch (e: any) {
            setError('Transcription error. Please try recording again.');
          }
        };
        mr.start();
        setIsRecording(true);
      } else {
        mediaRecorderRef.current?.stop();
        mediaRecorderRef.current?.stream.getTracks().forEach(t => t.stop());
        setIsRecording(false);
        try { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); } catch {}
        rafIdRef.current = null;
        try { await audioContextRef.current?.close(); } catch {}
        audioContextRef.current = null;
        analyserRef.current = null;
        recordingStartRef.current = null;
        setRecordingMs(0);
        setAudioLevel(0);
      }
    } catch (e: any) {
      const errorMessage = e?.message || 'Unknown error';
      if (errorMessage.includes('permission') || errorMessage.includes('Permission')) {
        setError('Microphone permission denied. Please allow microphone access in your browser settings and try again.');
      } else if (errorMessage.includes('not found') || errorMessage.includes('NotFoundError')) {
        setError('No microphone found. Please connect a microphone and try again.');
      } else if (errorMessage.includes('not allowed') || errorMessage.includes('NotAllowedError')) {
        setError('Microphone access denied. Please allow microphone access and try again.');
      } else {
        setError(`Voice search error: ${errorMessage}. Please try again.`);
      }
      setIsRecording(false);
      setRecordingMs(0);
      setAudioLevel(0);
    }
  };

  const validateSearchBeforeNavigation = async (
    textToSearch: string,
    computedMode: 'text' | 'hybrid'
  ): Promise<{ hasMissingFields: boolean; errorData?: any; networkError?: string }> => {
    if (computedMode === 'hybrid') {
      // For hybrid, we can't do full validation before upload, but we check connectivity
      try {
        const backendUrl = getBackendUrl();
        await fetch(`${backendUrl}/api/search/heartbeat`, { method: 'GET' });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('ERR_CONNECTION_REFUSED') ||
            errorMessage.includes('ECONNREFUSED') ||
            errorMessage.includes('NetworkError') ||
            errorMessage.includes('Network request failed')) {
          return {
            hasMissingFields: false,
            networkError: 'Unable to connect to the server. Please check if the services are running and try again.',
          };
        }
        return {
          hasMissingFields: false,
          networkError: 'Something went wrong while processing your request. Please try again later.',
        };
      }
      return { hasMissingFields: false };
    }

    const validationStartTime = performance.now();
    console.log(`[Timing] Starting validation for query: "${textToSearch}"`);

    try {
      const backendUrl = getBackendUrl();
      const headers: HeadersInit = getApiKeyHeaders();

      const apiCallStartTime = performance.now();
      console.log(`[Timing] Making API call to /api/search/validate at ${new Date().toISOString()}`);

      // Use optimized validation endpoint (skips catalog search - saves 1.6-3.1s)
      const response = await fetch(`${backendUrl}/api/search/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query: textToSearch })
      });

      const apiCallEndTime = performance.now();
      const apiCallDuration = apiCallEndTime - apiCallStartTime;
      console.log(`[Timing] API call completed in ${apiCallDuration.toFixed(2)}ms (${(apiCallDuration / 1000).toFixed(2)}s)`);

      if (!response.ok) {
        const parseStartTime = performance.now();
        const errorData = await response.json();
        const parseDuration = performance.now() - parseStartTime;
        console.log(`[Timing] Response parsing took ${parseDuration.toFixed(2)}ms`);
        
        // Handle validation endpoint errors
        const hasMissingFields = errorData?.hasMissingFields ||
                                 errorData?.missingFields?.length > 0 ||
                                 errorData?.missingMMVFY?.length > 0 ||
                                 errorData?.missingSubAggregate?.length > 0;
        if (hasMissingFields) {
          const totalDuration = performance.now() - validationStartTime;
          console.log(`[Timing] ✅ Validation complete - Missing fields detected. Total time: ${totalDuration.toFixed(2)}ms (${(totalDuration / 1000).toFixed(2)}s)`);
          console.log(`[Timing] Breakdown: API call=${apiCallDuration.toFixed(2)}ms, Parse=${parseDuration.toFixed(2)}ms`);
          // Transform validation response to match expected format
          return {
            hasMissingFields: true,
            errorData: {
              detail: {
                missingFields: errorData.missingFields || [],
                missingMMVFY: errorData.missingMMVFY || [],
                missingSubAggregate: errorData.missingSubAggregate || [],
                normalizedQuery: errorData.normalizedQuery || {},
                requiredFields: errorData.requiredFields || {},
                detectedParts: errorData.detectedParts || [],
                isMultipartQuery: errorData.isMultipartQuery || false,
                // Vehicle validation fields
                detectedVehicleNumber: errorData.detectedVehicleNumber || null,
                vehicleValidationFailed: errorData.vehicleValidationFailed || false,
                vehicleValidationError: errorData.vehicleValidationError || null,
                // Part number detection
                detectedPartNumber: errorData.detectedPartNumber || null,
                // Correlation ID for linking log entries
                correlationId: errorData.correlation_id || null
              }
            }
          };
        }
      } else if (response.ok) {
        const parseStartTime = performance.now();
        const validationData = await response.json();
        const parseDuration = performance.now() - parseStartTime;
        console.log(`[Timing] Response parsing took ${parseDuration.toFixed(2)}ms`);

        // Check if validation endpoint returned missing fields
        if (validationData?.hasMissingFields) {
          const totalDuration = performance.now() - validationStartTime;
          console.log(`[Timing] ✅ Validation complete - Missing fields detected. Total time: ${totalDuration.toFixed(2)}ms (${(totalDuration / 1000).toFixed(2)}s)`);
          console.log(`[Timing] Breakdown: API call=${apiCallDuration.toFixed(2)}ms, Parse=${parseDuration.toFixed(2)}ms`);
          return {
            hasMissingFields: true,
            errorData: {
              detail: {
                missingFields: validationData.missingFields || [],
                missingMMVFY: validationData.missingMMVFY || [],
                missingSubAggregate: validationData.missingSubAggregate || [],
                normalizedQuery: validationData.normalizedQuery || {},
                requiredFields: validationData.requiredFields || {},
                detectedParts: validationData.detectedParts || [],
                isMultipartQuery: validationData.isMultipartQuery || false,
                // Vehicle validation fields
                detectedVehicleNumber: validationData.detectedVehicleNumber || null,
                vehicleValidationFailed: validationData.vehicleValidationFailed || false,
                vehicleValidationError: validationData.vehicleValidationError || null,
                // Part number detection
                detectedPartNumber: validationData.detectedPartNumber || null,
                // Correlation ID for linking log entries
                correlationId: validationData.correlation_id || null
              }
            }
          };
        } else {
          const totalDuration = performance.now() - validationStartTime;
          console.log(`[Timing] ✅ Validation complete - No missing fields. Total time: ${totalDuration.toFixed(2)}ms (${(totalDuration / 1000).toFixed(2)}s)`);
        }
      }
    } catch (error) {
      const totalDuration = performance.now() - validationStartTime;
      console.error(`[Timing] ❌ Pre-validation error after ${totalDuration.toFixed(2)}ms:`, error);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Detect network/connection errors — backend is unreachable
      if (errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('ERR_CONNECTION_REFUSED') ||
          errorMessage.includes('ECONNREFUSED') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Network request failed')) {
        return {
          hasMissingFields: false,
          networkError: 'Unable to connect to the server. Please check if the services are running and try again.',
        };
      }

      // For other unexpected errors (timeout, CORS, etc.)
      return {
        hasMissingFields: false,
        networkError: `Something went wrong while processing your request. Please try again later.`,
      };
    }

    const totalDuration = performance.now() - validationStartTime;
    console.log(`[Timing] Validation finished. Total time: ${totalDuration.toFixed(2)}ms (${(totalDuration / 1000).toFixed(2)}s)`);
    return { hasMissingFields: false };
  };

  // Helper function to sanitize vehicle make (remove company suffixes)
  const sanitizeMake = (make: string): string => {
    if (!make) return make;
    // Remove common company suffixes
    const suffixes = [
      'INDIA PVT LTD',
      'INDIA LIMITED',
      'PVT LTD',
      'LIMITED',
      'LTD',
      'AUTO',
      'MOTORS',
      'PVT'
    ];
    let sanitized = make.trim().toUpperCase();
    suffixes.forEach(suffix => {
      const regex = new RegExp(`\\s+${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
      sanitized = sanitized.replace(regex, '').trim();
    });
    return sanitized;
  };

  // Helper function to sanitize vehicle model (remove variant/engine codes)
  const sanitizeModel = (model: string, make?: string): string => {
    if (!model) return model;
    let sanitized = model.trim().toUpperCase();
    
    // Remove make name from model if it appears (e.g., "SKODA OCTAVIA" -> "OCTAVIA" if make is "SKODA")
    if (make) {
      const makeUpper = make.toUpperCase();
      const makeRegex = new RegExp(`^${makeUpper}\\s+`, 'i');
      sanitized = sanitized.replace(makeRegex, '').trim();
    }
    
    // Remove common variant/engine codes (keep only main model name)
    // This is a simple approach - backend AI should do better, but this helps
    const variantPatterns = [
      /\s+\d+\.\d+\s*[A-Z]+\s*[A-Z]+\s*[A-Z]+\s*[A-Z]+\s*[A-Z]+\s*[A-Z]+/i, // e.g., "1.2 TIVCT P TIT MT"
      /\s+[A-Z]{2,}\s+[A-Z]{2,}\s+[A-Z]{2,}\s+[A-Z]{2,}/i, // Multiple uppercase words (likely variant codes)
      /\s+BS\s+\d+/i, // BS4, BS6, etc.
      /\s+AT\s+/i, // Automatic transmission indicator
      /\s+MT\s+/i, // Manual transmission indicator
    ];
    
    variantPatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '').trim();
    });
    
    return sanitized;
  };

  const validateVehicleNumber = (vehicleNum: string): { isValid: boolean; error: string } => {
    if (!vehicleNum.trim()) {
      return { isValid: false, error: '' }; // No error for empty, just not valid
    }
    
    // Normalize vehicle number (remove spaces, hyphens, underscores)
    const normalized = vehicleNum.replace(/[\s\-_]/g, '').toUpperCase();
    
    // Validate format (basic check)
    const standardPattern = /^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/;
    const bharatPattern = /^[0-9]{2}BH[0-9]{4}[A-Z]{1,2}$/;
    
    if (standardPattern.test(normalized) || bharatPattern.test(normalized)) {
      return { isValid: true, error: '' };
    }
    
    // Check if it's partially valid (user is still typing)
    const partialPattern = /^[A-Z]{0,2}[0-9]{0,2}[A-Z]{0,2}[0-9]{0,4}$|^[0-9]{0,2}BH[0-9]{0,4}[A-Z]{0,2}$/;
    if (partialPattern.test(normalized)) {
      return { isValid: false, error: '' }; // Still typing, no error yet
    }
    
    return { isValid: false, error: 'Invalid format. Use: AA00AA0000 or 22BH1234AB' };
  };

  // Handler for vehicle number input
  // Can be set from user input or auto-detected from search query
  const handleVehicleNumberChange = (value: string) => {
    setVehicleNumber(value);
    const validation = validateVehicleNumber(value);
    if (validation.error) {
      setVehicleNumberError(validation.error);
      setVehicleDetailsFromNumber(false); // Reset if invalid
    } else {
      setVehicleNumberError('');
    }
    // Reset vehicleDetailsFromNumber when user clears the number
    if (!value.trim()) {
      setVehicleDetailsFromNumber(false);
    }
  };

  // Debounced auto-fetch vehicle details (1.5 seconds after user stops typing)
  useEffect(() => {
    if (!vehicleNumber.trim()) {
      return;
    }

    // Validate format before fetching
    const validation = validateVehicleNumber(vehicleNumber);
    if (!validation.isValid) {
      return; // Don't fetch if format is invalid
    }

    // Set up debounce timer
    const timer = setTimeout(() => {
      fetchVehicleDetailsFromNumber(vehicleNumber);
    }, 1500); // 1.5 seconds delay

    // Cleanup timer on unmount or when vehicleNumber changes
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleNumber]); // fetchVehicleDetailsFromNumber is stable, no need to include in deps

  const fetchVehicleDetailsFromNumber = async (vehicleNum: string) => {
    if (!vehicleNum.trim()) {
      setVehicleNumberError('Please enter a vehicle number');
      return;
    }
    
    // Normalize vehicle number (remove spaces, hyphens, underscores)
    const normalized = vehicleNum.replace(/[\s\-_]/g, '').toUpperCase();
    
    // Validate format (basic check)
    const validation = validateVehicleNumber(vehicleNum);
    if (!validation.isValid) {
      setVehicleNumberError(validation.error || 'Invalid vehicle number format. Use format like TN10AB1234 or 22BH1234AB');
      return;
    }
    
    setIsFetchingVehicleDetails(true);
    setVehicleNumberError('');
    
    try {
      const backendUrl = getBackendUrl();
      const headers: HeadersInit = getApiKeyHeaders();

      const response = await fetch(`${backendUrl}/api/catalog/vehicle/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ vehicle_number: normalized })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Failed to fetch vehicle details (Status: ${response.status})`;
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      const vehicleData = data?.data || data || {};
      
      // Log debug information about variant extraction from Vahan API
      if (vehicleData._debug) {
        console.log('[Vehicle Validation Debug]', {
          vehicleNumber: normalized,
          variantExtracted: vehicleData.variant,
          rawApiKeys: vehicleData._debug.raw_api_keys,
          variantRelatedKeys: vehicleData._debug.variant_related_keys,
          variantRelatedValues: vehicleData._debug.variant_related_values
        });
        
        // If variant-related keys exist but variant is None, log warning
        if (vehicleData._debug.variant_related_keys && vehicleData._debug.variant_related_keys.length > 0 && !vehicleData.variant) {
          console.warn('[Vehicle Validation] Variant-related keys found in Vahan API but variant is None:', {
            keys: vehicleData._debug.variant_related_keys,
            values: vehicleData._debug.variant_related_values
          });
        }
      }
      
      if (vehicleData.success && (vehicleData.make || vehicleData.model)) {
        // Check if vehicle is electric - check multiple sources
        const fuelTypeUpper = vehicleData.fuel_type ? String(vehicleData.fuel_type).toUpperCase().trim() : '';
        const isElectricValue = vehicleData.is_electric;
        
        // Check for various electric vehicle indicators
        const electricFuelTypes = [
          "ELECTRIC", "EV", "ELECTRIC VEHICLE", "E-ELECTRIC",
          "PURE EV", "BATTERY ELECTRIC", "BEV", "ELECTRIC BATTERY"
        ];
        
        const isElectric = Boolean(
          isElectricValue === true || 
          isElectricValue === "true" ||
          String(isElectricValue).toUpperCase() === "TRUE" ||
          electricFuelTypes.includes(fuelTypeUpper) ||
          (fuelTypeUpper && (fuelTypeUpper.includes("ELECTRIC") || fuelTypeUpper.includes("EV")))
        );
        
        setIsElectricVehicle(isElectric);
        
        // Extract vehicle_class from API response (check multiple possible field names)
        // Backend returns vehicle_type, but Vahan API may return vehicle_class
        const vehicleClassValue = vehicleData.vehicle_class || vehicleData.vehicleClass || vehicleData.vehicle_type || vehicleData.vehicleType || vehicleData.class || '';
        const vehicleClassUpper = vehicleClassValue ? String(vehicleClassValue).toUpperCase().trim() : '';
        setVehicleClass(vehicleClassUpper);
        
        // Auto-fill vehicle details
        const updatedDetails = {
          make: vehicleData.make || vehicleDetails.make,
          model: vehicleData.model || vehicleDetails.model,
          variant: vehicleData.variant || vehicleDetails.variant,
          fuelType: vehicleData.fuel_type || vehicleDetails.fuelType,
          year: vehicleData.year ? String(vehicleData.year) : vehicleDetails.year
        };
        
        setVehicleDetails(prev => ({
          ...prev,
          ...updatedDetails
        }));
        
        // Mark that details were fetched from vehicle number
        setVehicleDetailsFromNumber(true);

        // NOTE: We intentionally do NOT remove MMVFY fields from missingFieldsModal here.
        // This keeps the Vehicle Details section visible so users can see and verify
        // the fetched values before proceeding with the search.
        
        // If make is set, fetch models
        if (vehicleData.make && vehicleMakes.length === 0) {
          fetchVehicleMakes();
        }
        if (vehicleData.make) {
          fetchVehicleModels(vehicleData.make);
        }
        if (vehicleData.make && vehicleData.model) {
          fetchVehicleVariants(vehicleData.make, vehicleData.model);
          // Fetch years with all available vehicle context from Vahan
          fetchVehicleYears(
            vehicleData.make,
            vehicleData.model,
            vehicleData.variant || undefined,
            vehicleData.fuelType || undefined
          );
        }

        setVehicleNumberError('');
      } else {
        setVehicleDetailsFromNumber(false);
        setIsElectricVehicle(false);
        setVehicleClass('');
        
        // Even if API fails, try to extract make/model from normalized query if available
        const normalized = missingFieldsModal.normalizedQuery || {};
        
        // Normalize make name for display (MARUTI -> MARUTI SUZUKI)
        const normalizeMakeForDisplay = (make: string): string => {
          if (!make) return make;
          const makeUpper = make.toUpperCase().trim();
          const makeMapping: Record<string, string> = {
            "MARUTI": "MARUTI SUZUKI",
            "SUZUKI": "MARUTI SUZUKI",
            "MERCEDES": "MERCEDES-BENZ",
            "BENZ": "MERCEDES-BENZ",
          };
          return makeMapping[makeUpper] || make;
        };
        
        if (normalized.make || normalized.model) {
          const normalizedMake = normalizeMakeForDisplay(normalized.make || '');
          setVehicleDetails(prev => ({
            ...prev,
            make: normalizedMake || prev.make,
            model: normalized.model || prev.model,
            variant: normalized.variant || prev.variant,
            fuelType: normalized.fuelType || prev.fuelType,
            year: normalized.year ? String(normalized.year) : prev.year
          }));
          
          // If make is available, fetch models for dropdown (use normalized make)
          if (normalizedMake) {
            fetchVehicleModels(normalizedMake);
          }
          if (normalizedMake && normalized.model) {
            fetchVehicleVariants(normalizedMake, normalized.model);
            fetchVehicleYears(
              normalizedMake,
              normalized.model,
              normalized.variant || undefined,
              normalized.fuelType || undefined
            );
          }

          setVehicleNumberError('Vehicle details not found in database, but using detected values from your search. Please verify and complete the form.');
        } else {
          setVehicleNumberError(vehicleData.error || 'Vehicle details not found. Please enter manually.');
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle details:', error);
      let errorMessage = 'Failed to fetch vehicle details. Please enter manually.';
      
      if (error instanceof Error) {
        // Check for network errors
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Vehicle validation service not found. Please contact support.';
        } else if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Authentication failed. Please refresh the page and try again.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error. The vehicle validation service is temporarily unavailable.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setVehicleNumberError(errorMessage);
    } finally {
      setIsFetchingVehicleDetails(false);
    }
  };

  const handleVehicleDetailChange = (field: keyof VehicleDetailsForm, value: string) => {
    // Year is now a dropdown - just set the value directly
    if (field === 'year') {
      setYearError('');
      setVehicleDetails(prev => ({ ...prev, year: value }));
      return;
    }

    setVehicleDetails(prev => {
      const updated = { ...prev, [field]: value };

      // Cascading logic: when Make changes, clear Model/Variant/FuelType/Year and fetch Models
      if (field === 'make') {
        updated.model = '';
        updated.variant = '';
        updated.fuelType = '';
        updated.year = '';
        setVehicleModels([]);
        setVehicleVariants([]);
        setVehicleFuelTypes([]);
        setVehicleYears([]);
        if (value) {
          fetchVehicleModels(value);
        }
      }

      // When Model changes, clear Variant/FuelType/Year, fetch Variants + FuelTypes + Years
      if (field === 'model') {
        updated.variant = '';
        updated.fuelType = '';
        updated.year = '';
        setVehicleVariants([]);
        setVehicleFuelTypes([]);
        setVehicleYears([]);
        if (value && updated.make) {
          console.log(`[handleVehicleDetailChange] Model changed to "${value}", fetching variants for make="${updated.make}"`);
          fetchVehicleVariants(updated.make, value);
          // Fetch broad years for make+model
          fetchVehicleYears(updated.make, value);
        }
      }

      // When Variant changes, clear selected year and try filtered re-fetch
      // Keep existing vehicleYears as fallback (make+model years) if filtered query returns empty
      if (field === 'variant') {
        updated.year = '';
        if (value && updated.make && updated.model) {
          console.log(`[handleVehicleDetailChange] Variant changed to "${value}", re-fetching years`);
          fetchVehicleYears(updated.make, updated.model, value, updated.fuelType || undefined);
        }
      }

      // When FuelType changes, clear selected year and try filtered re-fetch
      // Keep existing vehicleYears as fallback (make+model years) if filtered query returns empty
      if (field === 'fuelType') {
        updated.year = '';
        if (value && updated.make && updated.model) {
          console.log(`[handleVehicleDetailChange] FuelType changed to "${value}", re-fetching years`);
          fetchVehicleYears(updated.make, updated.model, updated.variant || undefined, value);
        }
      }

      return updated;
    });
  };

  const handleVehicleDetailsSubmit = async (event: FormEvent) => {
    event.preventDefault();
    
    // Validate required fields
    const fieldLabels: Record<keyof VehicleDetailsForm, string> = {
      make: 'Make',
      model: 'Model',
      variant: 'Variant',
      fuelType: 'Fuel Type',
      year: 'Year',
      subAggregate: 'Sub Aggregate',
      partNumber: 'Part Number'
    };
    
    // If vehicle number is provided and details were fetched, skip strict validation
    // Vehicle number data takes priority and will be sent to backend
    const shouldSkipStrictValidation = vehicleNumber.trim() && vehicleDetailsFromNumber;
    
    // Validate MMVFY fields if they are missing (skip if vehicle number was used)
    // If make is missing, model is also required (they go together)
    // Variant is always required if any MMVFY field is missing
    const missingMMVFYFields = shouldSkipStrictValidation ? [] : Object.entries(vehicleDetails)
      .filter(([key, value]) => {
        // Variant is always required if any MMVFY field is missing (case-insensitive check)
        if (key === 'variant') {
          const variantMissing = missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'variant');
          const anyMMVFYMissing = missingFieldsModal.missingMMVFY.length > 0;
          return (variantMissing || anyMMVFYMissing) && !value.trim();
        }
        // If make is missing, model is also required
        if (key === 'model') {
          const modelMissing = missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'model');
          const makeMissing = missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'make');
          return (modelMissing || makeMissing) && !value.trim();
        }
        // Case-insensitive check for other fields
        return missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === key.toLowerCase()) && !value.trim();
      })
      .map(([key]) => fieldLabels[key as keyof VehicleDetailsForm] || key);
    
    // Validate SubAggregate if it's missing
    // Skip subAggregate validation if an image is uploaded (image will be processed to extract part info)
    // Also skip if parts are selected via modalSelectedParts or partNumber is provided
    const hasImageUploaded = selectedFiles.length > 0;
    const hasPartsSelected = modalSelectedParts.length > 0 || vehicleDetails.subAggregate.trim().length > 0;
    const hasPartNumber = partNumberInputValue.trim().length > 0;
    const missingPartFields = missingFieldsModal.missingSubAggregate.includes('subAggregate') && !hasPartsSelected && !hasImageUploaded && !hasPartNumber
      ? ['Part Name']
      : [];

    const allMissingUserFields = [...missingMMVFYFields, ...missingPartFields];

    if (allMissingUserFields.length > 0) {
      setError(`Please fill in the required fields: ${allMissingUserFields.join(', ')}`);
      return;
    }

    // Also check if part image is required (if subAggregate is missing and no image uploaded)
    // This is a redundant check but kept for clarity - if we reach here, either parts are selected, partNumber is provided, or image is uploaded
    if (missingFieldsModal.missingSubAggregate.includes('subAggregate') && !hasPartsSelected && !hasImageUploaded && !hasPartNumber) {
      setError('Please provide either part name, part number, or upload a part image');
      return;
    }
    
    // Validate year selection (dropdown - just check if selected when required)
    if (missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'year')) {
      if (!vehicleDetails.year || vehicleDetails.year.trim().length === 0) {
        setError('Please select a Year from the dropdown');
        setYearError('Year is required');
        return;
      }
    }
    
    // Helper function to extract variant-like patterns from query when variant is missing
    // This is a local version for the submit handler - uses the top-level function
    // No need to redefine, just use the one defined at component level
    
    // Build augmented query
    // If vehicle number is provided and details were fetched from it, prioritize vehicle number data
    let cleanedSearchText = missingFieldsModal.searchQuery || searchText;
    
    // CRITICAL FIX: If variant is missing but we have the original query, try to extract variant from it
    // This prevents the redirect loop when variant is not properly extracted by AI
    let finalVariant = vehicleDetails.variant;
    if (!finalVariant.trim() && cleanedSearchText && vehicleDetails.make && vehicleDetails.model) {
      const extractedVariant = extractVariantFromQuery(cleanedSearchText, vehicleDetails.make, vehicleDetails.model);
      if (extractedVariant) {
        // Update both the local variable and the state
        finalVariant = extractedVariant;
        vehicleDetails.variant = extractedVariant;
        setVehicleDetails(prev => ({ ...prev, variant: extractedVariant }));
        console.log(`[Variant Extraction] Extracted variant "${extractedVariant}" from query: "${cleanedSearchText}"`);
      } else {
        console.log(`[Variant Extraction] Failed to extract variant from query: "${cleanedSearchText}"`);
      }
    }
    
    // If vehicle number is provided (either from query detection or user input), include it in the query
    // Use vehicle number if it was detected from query OR if details were fetched from it
    if (vehicleNumber.trim() && (vehicleNumberFromQuery || vehicleDetailsFromNumber)) {
      // Normalize vehicle number for query
      const normalizedVehicleNumber = vehicleNumber.replace(/[\s\-_]/g, '').toUpperCase();
      
      // Build query with vehicle number first, then vehicle details from number, then part info
    const parts: string[] = [];
    const pushPart = (value?: string) => {
      if (value && value.trim()) {
        parts.push(value.trim());
      }
    };
    
      // Add vehicle number first (highest priority)
      pushPart(normalizedVehicleNumber);
      
      // Add vehicle details from vehicle number (these override any manually selected values)
      // Only add if they exist and are not empty
      // Note: Backend should sanitize these, but we'll use them as-is
      if (vehicleDetails.make) {
    pushPart(vehicleDetails.make);
      }
      if (vehicleDetails.model) {
    pushPart(vehicleDetails.model);
      }
      // Variant and fuel type are optional, only add if they exist
      if (vehicleDetails.variant) {
    pushPart(vehicleDetails.variant);
      }
      if (vehicleDetails.fuelType) {
    pushPart(vehicleDetails.fuelType);
      }
      if (vehicleDetails.year) {
    pushPart(vehicleDetails.year);
      }
      
      // Add part information
      if (vehicleDetails.subAggregate) {
    pushPart(vehicleDetails.subAggregate);
      }
      
      // Add cleaned search text (without vehicle terms) at the end
      if (cleanedSearchText) {
        // Remove vehicle-related terms from search text to avoid duplication
        // Include both individual terms and combined terms for better matching
        // Use finalVariant if it was extracted, otherwise use vehicleDetails.variant
        const variantToUseForCleaning = finalVariant || vehicleDetails.variant;
        const vehicleTerms = [
          vehicleDetails.make,
          vehicleDetails.model,
          variantToUseForCleaning,
          vehicleDetails.fuelType,
          vehicleDetails.year,
          normalizedVehicleNumber
        ].filter((term): term is string => !!term && term.trim() !== '').map(term => term.trim());
        
        // Also create combined terms (e.g., "ford figo aspire" to match against)
        const combinedTerms: string[] = [];
        if (vehicleDetails.make && vehicleDetails.model) {
          combinedTerms.push(`${vehicleDetails.make} ${vehicleDetails.model}`.toLowerCase());
        }
        if (vehicleDetails.make) {
          combinedTerms.push(vehicleDetails.make.toLowerCase());
        }
        if (vehicleDetails.model) {
          combinedTerms.push(vehicleDetails.model.toLowerCase());
        }
        
        // Remove individual vehicle terms (case-insensitive, whole word)
        vehicleTerms.forEach(term => {
          if (term) {
            const regex = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
            cleanedSearchText = cleanedSearchText.replace(regex, '').trim();
          }
        });
        
        // Remove combined terms (case-insensitive, can be part of phrase)
        combinedTerms.forEach(term => {
          if (term && cleanedSearchText.toLowerCase().includes(term)) {
            // Remove the combined term and surrounding words if they match
            const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            cleanedSearchText = cleanedSearchText.replace(regex, '').trim();
          }
        });
        
        // Clean up extra spaces and remove common vehicle-related words
        const commonVehicleWords = ['ford', 'figo', 'aspire', 'brakepad', 'brake', 'pad'];
        const words = cleanedSearchText.toLowerCase().split(/\s+/);
        const filteredWords = words.filter(word => {
          // Keep the word if it's not a common vehicle word or if it's part of a compound term
          return !commonVehicleWords.some(vw => word.includes(vw) || vw.includes(word));
        });
        cleanedSearchText = filteredWords.join(' ').trim();
        
        cleanedSearchText = cleanedSearchText.replace(/\s+/g, ' ').trim();
        
        // Only add if there's meaningful content left (not just common words)
        if (cleanedSearchText && cleanedSearchText.length > 2) {
          pushPart(cleanedSearchText);
        }
      }
    
    // Deduplicate while preserving order
    const seen = new Set<string>();
    const uniqueParts = parts.filter(part => {
      const key = part.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    
    const augmentedQuery = uniqueParts.join(' ').replace(/\s+/g, ' ').trim();
    
    // Close modal and navigate
      setMissingFieldsModal({ isOpen: false, missingFields: [], missingMMVFY: [], missingSubAggregate: [], normalizedQuery: {}, source: null, searchQuery: '' });
      setYearError('');
      setError('');
      setPartValidationError(null);
      setPartValidationSuggestions([]);

      // Navigate with augmented query using URL params
      // correlation_id is managed by backend via Redis + session_id
      const computedMode: 'text' | 'hybrid' =
        (augmentedQuery.trim() && selectedFiles.length > 0)
          ? 'hybrid'
          : 'text';

      navigate(buildSearchUrl({ q: augmentedQuery, mode: computedMode }));
      return;
    }
    
    // Original logic for when vehicle number is not provided
    const vehicleTerms = [
      vehicleDetails.make,
      vehicleDetails.model,
      vehicleDetails.variant,
      vehicleDetails.fuelType,
      vehicleDetails.year,
      vehicleDetails.subAggregate
    ].filter((term): term is string => !!term && term.trim() !== '').map(term => term.trim());
    
    // Remove vehicle terms from searchText (case-insensitive)
    vehicleTerms.forEach(term => {
      if (term) {
        // Create regex to match the term as a whole word (case-insensitive)
        const regex = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        cleanedSearchText = cleanedSearchText.replace(regex, '').trim();
      }
    });
    
    // Clean up extra spaces
    cleanedSearchText = cleanedSearchText.replace(/\s+/g, ' ').trim();
    
    const parts: string[] = [];
    const pushPart = (value?: string) => {
      if (value && value.trim()) {
        parts.push(value.trim());
      }
    };
    
    // Add vehicle details first
    // Use finalVariant if it was extracted, otherwise use vehicleDetails.variant
    const variantToUseForQuery = finalVariant || vehicleDetails.variant;
    pushPart(vehicleDetails.make);
    pushPart(vehicleDetails.model);
    pushPart(variantToUseForQuery);
    pushPart(vehicleDetails.fuelType);
    pushPart(vehicleDetails.year);
    pushPart(vehicleDetails.subAggregate);
    
    // Add cleaned search text (without vehicle terms) at the end
    if (cleanedSearchText) {
      pushPart(cleanedSearchText);
    }
    
    // Deduplicate while preserving order
    const seen = new Set<string>();
    const uniqueParts = parts.filter(part => {
      const key = part.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    
    const augmentedQuery = uniqueParts.join(' ').replace(/\s+/g, ' ').trim();
    
    // Log the augmented query for debugging
    console.log('[Continue Search] Augmented query:', augmentedQuery);
    console.log('[Continue Search] Vehicle details:', {
      make: vehicleDetails.make,
      model: vehicleDetails.model,
      variant: finalVariant || vehicleDetails.variant,
      fuelType: vehicleDetails.fuelType,
      year: vehicleDetails.year,
      subAggregate: vehicleDetails.subAggregate
    });
    console.log('[Continue Search] Final variant used in query:', finalVariant || vehicleDetails.variant || '(empty)');
    console.log('[Continue Search] Original vehicleDetails.variant:', vehicleDetails.variant);
    
    // Close modal and navigate
    // correlation_id is managed by backend via Redis + session_id
    setMissingFieldsModal({ isOpen: false, missingFields: [], missingMMVFY: [], missingSubAggregate: [], normalizedQuery: {}, source: null, searchQuery: '' });
    setError('');
    setPartValidationError(null);
    setPartValidationSuggestions([]);

    // Check if this is from image upload
    const hasImageUpload = uploadedImageIds.length > 0;

    // Parse comma-separated part numbers for multi-search
    const partNumbers = partNumberInputValue
      .split(',')
      .map(pn => pn.trim())
      .filter(pn => pn.length > 0);

    // Check if multipart search (multiple part names OR multiple part numbers)
    const isMultiPartSearch = modalSelectedParts.length > 1 || partNumbers.length > 1;

    if (isMultiPartSearch) {
      // Navigate to multipart results page
      const urlParams = new URLSearchParams();

      // Include part names if selected
      if (modalSelectedParts.length > 0) {
        urlParams.set('parts', modalSelectedParts.join(','));
      }

      // Include part numbers if provided
      if (partNumbers.length > 0) {
        urlParams.set('partNumbers', partNumbers.join(','));
      }

      if (vehicleDetails.make) urlParams.set('make', vehicleDetails.make);
      if (vehicleDetails.model) urlParams.set('model', vehicleDetails.model);
      if (finalVariant || vehicleDetails.variant) urlParams.set('variant', finalVariant || vehicleDetails.variant);
      if (vehicleDetails.fuelType) urlParams.set('fuelType', vehicleDetails.fuelType);
      if (vehicleDetails.year) urlParams.set('year', vehicleDetails.year);
      // Pass correlation_id from validation to link log entries
      if (missingFieldsModal.correlationId) urlParams.set('correlationId', missingFieldsModal.correlationId);

      navigate(`/results?${urlParams.toString()}`);
      setUploadedImageIds([]);
      setModalSelectedParts([]);
      return;
    }

    // Single part search - use first selected part or vehicleDetails.subAggregate
    const singlePart = modalSelectedParts.length === 1 ? modalSelectedParts[0] : vehicleDetails.subAggregate;
    // Use first part number if only one provided
    const singlePartNumber = partNumbers.length === 1 ? partNumbers[0] : undefined;

    // Navigate with augmented query using URL params
    const computedMode: 'text' | 'hybrid' =
      (augmentedQuery.trim() && (selectedFiles.length > 0 || hasImageUpload))
        ? 'hybrid'
        : 'text';

    navigate(buildSearchUrl({
      q: augmentedQuery,
      mode: computedMode,
      imageIds: hasImageUpload ? uploadedImageIds : undefined,
      make: vehicleDetails.make || undefined,
      model: vehicleDetails.model || undefined,
      variant: (finalVariant || vehicleDetails.variant) || undefined,
      fuelType: vehicleDetails.fuelType || undefined,
      year: vehicleDetails.year || undefined,
      subAggregate: singlePart || undefined,
      partNumber: singlePartNumber || undefined,
    }));

    // Clear uploaded imageIds after navigation
    setUploadedImageIds([]);
    setModalSelectedParts([]);
  };

  const handleCloseModal = () => {
    setMissingFieldsModal({ isOpen: false, missingFields: [], missingMMVFY: [], missingSubAggregate: [], normalizedQuery: {}, source: null, searchQuery: '' });
    // Clear validation errors
    setPartValidationError(null);
    setPartValidationSuggestions([]);
    setVehicleDetails({
      make: '',
      model: '',
      variant: '',
      fuelType: '',
      year: '',
      subAggregate: '',
      partNumber: ''
    });
    setVehicleNumber('');
    setVehicleNumberError('');
    setVehicleDetailsFromNumber(false);
    setIsElectricVehicle(false);
    setVehicleClass('');
    setVehicleNumberFromQuery(false);
    setVehicleModels([]);
    setVehicleVariants([]);
    setVehicleFuelTypes([]);
    setSubAggregateInputValue('');
    setSubAggregateSuggestions([]);
    setSubAggregateInlineSuggestion('');
    setPartNumberInputValue('');
    setModalSelectedParts([]);
    setError('');
    setIsSearching(false);
    setUploadedImageIds([]);
  };

  // Debounced sub aggregate search with inline suggestion
  useEffect(() => {
    if (!missingFieldsModal.isOpen) {
      setSubAggregateInlineSuggestion('');
      return;
    }
    
    const handler = setTimeout(() => {
      if (subAggregateInputValue.trim().length > 0) {
        fetchSubAggregates(subAggregateInputValue);
      } else {
        // Clear inline suggestion when input is empty
        setSubAggregateInlineSuggestion('');
        // Optionally fetch all when input is cleared
        fetchSubAggregates();
      }
    }, 300);
    
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subAggregateInputValue, missingFieldsModal.isOpen]);
  
  // Sync subAggregateInputValue with vehicleDetails.subAggregate when it changes externally
  useEffect(() => {
    if (missingFieldsModal.isOpen && vehicleDetails.subAggregate !== subAggregateInputValue) {
      setSubAggregateInputValue(vehicleDetails.subAggregate);
      setSubAggregateInlineSuggestion('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleDetails.subAggregate, missingFieldsModal.isOpen]);

  // Sync partNumberInputValue with vehicleDetails.partNumber when it changes externally
  useEffect(() => {
    if (missingFieldsModal.isOpen && vehicleDetails.partNumber !== partNumberInputValue) {
      setPartNumberInputValue(vehicleDetails.partNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleDetails.partNumber, missingFieldsModal.isOpen]);
  
  const handleSubAggregateInputChange = (value: string) => {
    setSubAggregateInputValue(value);
    setVehicleDetails(prev => ({ ...prev, subAggregate: value }));
  };

  const handlePartNumberInputChange = (value: string) => {
    setPartNumberInputValue(value);
    setVehicleDetails(prev => ({ ...prev, partNumber: value }));
  };

  const handleSearch = async (transcribedText?: string) => {
    const textToSearch = transcribedText || searchText;
    
    // Auto-detected mode (only text and hybrid supported)
    const computedMode: 'text' | 'hybrid' =
      (textToSearch.trim() && selectedFiles.length > 0)
        ? 'hybrid'
        : 'text';

    // Validation based on computed mode
    let validationError = '';
    if (computedMode === 'text') {
      if (!textToSearch.trim()) {
        validationError = 'Please enter text to search';
      }
    } else if (computedMode === 'hybrid') {
      if (!textToSearch.trim() || selectedFiles.length === 0) {
        validationError = 'Please enter text and upload at least one image';
      }
    }
    
    // If only images are selected without text, show error
    if (selectedFiles.length > 0 && !textToSearch.trim()) {
      validationError = 'Please enter ';
    }
    
    if (validationError) {
      setError(validationError);
      return;
    }
    
    if (!textToSearch.trim() && selectedFiles.length === 0) {
      setError('Looking for something specific? Type it in, or upload a part image — it helps us find the best matches faster!');
      return;
    }

    setIsSearching(true);
    setError('');
    
    // Save to recent searches
    try {
      if (textToSearch.trim()) {
        const next = [textToSearch.trim(), ...recentSearches.filter(s => s !== textToSearch.trim())].slice(0, 8);
        setRecentSearches(next);
        localStorage.setItem('recent_searches', JSON.stringify(next));
      }
    } catch {}

    // Validate before navigation
    const validationStartTime = performance.now();
    console.log(`[Timing] Starting search validation at ${new Date().toISOString()}`);
    
    const validation = await validateSearchBeforeNavigation(textToSearch.trim(), computedMode);
    
    const validationDuration = performance.now() - validationStartTime;
    console.log(`[Timing] Search validation took ${validationDuration.toFixed(2)}ms (${(validationDuration / 1000).toFixed(2)}s)`);

    // Check for network/connection errors — prevent navigation and show error on search page
    if (validation.networkError) {
      setError(validation.networkError);
      setIsSearching(false);
      return;
    }

    if (validation.hasMissingFields && validation.errorData) {
      const detail = validation.errorData.detail || validation.errorData;
      const missingMMVFY = detail?.missingMMVFY || [];
      const missingSubAggregate = detail?.missingSubAggregate || [];
      const allMissing = detail?.missingFields || [...missingMMVFY, ...missingSubAggregate];
      
      // Use backend-detected vehicle number (preferred) or fallback to frontend detection
      const detectedVehicleNumber = detail?.detectedVehicleNumber || PartNumberDetector.detectVehicleNumber(textToSearch.trim());
      const vehicleValidationFailed = detail?.vehicleValidationFailed || false;
      const vehicleValidationError = detail?.vehicleValidationError || null;
      const normalized = detail.normalizedQuery || {};
      const detectedPartNumber = detail?.detectedPartNumber || normalized?.partNumber || null;
      const validationCorrelationId = detail?.correlationId || undefined;

      // If a part number is detected, bypass MMVFY and proceed directly to search
      // Part numbers can be searched without vehicle details - the backend will enrich via internet search
      if (detectedPartNumber) {
        console.log(`[Auto-proceed] Part number "${detectedPartNumber}" detected, bypassing MMVFY requirement and proceeding to search...`);
        setIsSearching(true);
        if (detectedVehicleNumber) {
          setVehicleNumber(detectedVehicleNumber);
          setVehicleNumberError('');
          setVehicleNumberFromQuery(true);
        }
        navigate(buildSearchUrl({ q: textToSearch.trim(), mode: computedMode }));
        return;
      }

      // Check if part information is detected (from normalized query or image upload)
      // Use the same validation logic as getValidSubAggregateDetectedValue
      const validSubAggregate = getValidSubAggregateDetectedValue(normalized);
      const hasPartInfo =
        (validSubAggregate && validSubAggregate.trim()) ||
        (normalized.partDescription && normalized.partDescription.trim() &&
         !containsVehicleDetails(normalized.partDescription, normalized)) ||
        selectedFiles.length > 0;

      // If both vehicle number and part information are detected AND validation succeeded, proceed directly
      // Do NOT auto-proceed if vehicle validation failed - show modal with error instead
      if (detectedVehicleNumber && hasPartInfo && !vehicleValidationFailed) {
        console.log('[Auto-proceed] Vehicle number and part information detected, fetching vehicle details and proceeding...');
        setIsSearching(true);

        // Set vehicle number and fetch details
        setVehicleNumber(detectedVehicleNumber);
        setVehicleNumberError('');
        setVehicleNumberFromQuery(true);

        try {
          // Fetch vehicle details from Vahan API (for frontend state)
          // Note: Backend will also detect vehicle number from search query and fetch details
          await fetchVehicleDetailsFromNumber(detectedVehicleNumber);
          
          console.log('[Auto-proceed] Vehicle details fetched, proceeding to search...');
          
          // Navigate to results page using URL params
          // Backend will detect vehicle number from search query and fetch details from Vahan API
          navigate(buildSearchUrl({ q: textToSearch.trim(), mode: computedMode }));
          return;
        } catch (error) {
          console.error('[Auto-proceed] Error fetching vehicle details, showing modal instead:', error);
          // If fetch fails, fall through to show modal
          setIsSearching(false);
        }
      }
      
      if (allMissing && allMissing.length > 0) {
        // Show modal for missing fields
        const modalSetupStartTime = performance.now();
        console.log(`[Timing] Setting up missing fields modal...`);
        
        // Map backend field names to frontend field names
        const fieldMapping: Record<string, keyof VehicleDetailsForm> = {
          'Make': 'make',
          'Model': 'model',
          'Variant': 'variant',
          'Fuel Type': 'fuelType',
          'Year': 'year',
          'Sub Aggregate': 'subAggregate'
        };
        
        const mappedMissingFields = allMissing.map((field: string) => fieldMapping[field] || field.toLowerCase());
        
        // Pre-fill form with normalized values
        // For subAggregate, only use if it doesn't contain vehicle details
        const validSubAggregate = getValidSubAggregateDetectedValue(normalized);
        
        // Normalize make name for display (MARUTI -> MARUTI SUZUKI)
        const normalizeMakeForDisplay = (make: string): string => {
          if (!make) return make;
          const makeUpper = make.toUpperCase().trim();
          const makeMapping: Record<string, string> = {
            "MARUTI": "MARUTI SUZUKI",
            "SUZUKI": "MARUTI SUZUKI",
            "MERCEDES": "MERCEDES-BENZ",
            "BENZ": "MERCEDES-BENZ",
          };
          return makeMapping[makeUpper] || make;
        };
        
        const normalizedMake = normalizeMakeForDisplay(normalized.make ?? '');
        
        // CRITICAL FIX: If variant is missing from normalized query, try to extract it from original query
        // This prevents the redirect loop when variant is not properly extracted by AI
        let variantValue = normalized.variant ?? '';
        if (!variantValue && textToSearch && normalizedMake && normalized.model) {
          const extractedVariant = extractVariantFromQuery(textToSearch, normalizedMake, normalized.model);
          if (extractedVariant) {
            variantValue = extractedVariant;
            console.log(`[Modal Setup] Extracted variant "${extractedVariant}" from query: "${textToSearch}"`);
          } else {
            console.log(`[Modal Setup] Failed to extract variant from query: "${textToSearch}"`);
          }
        } else if (variantValue) {
          console.log(`[Modal Setup] Using variant from normalized query: "${variantValue}"`);
        }
        
        const preFilledDetails: VehicleDetailsForm = {
          make: normalizedMake,
          model: normalized.model ?? '',
          variant: variantValue,
          fuelType: normalized.fuelType ?? '',
          year: normalized.year ? String(normalized.year) : '',
          subAggregate: validSubAggregate ?? '',
          partNumber: ''
        };
        
        setVehicleDetails(preFilledDetails);
        setSubAggregateInputValue(preFilledDetails.subAggregate);

        // Pre-fill modalSelectedParts with detected parts from multipart extraction
        // Priority: detectedParts (multiple) > subAggregate (single)
        const detectedParts = detail?.detectedParts || [];
        if (detectedParts.length > 0) {
          console.log(`[Modal Setup] Using detected parts from multipart extraction: ${detectedParts.join(', ')}`);
          setModalSelectedParts(detectedParts);
          // If first detected part differs from subAggregate, sync it
          if (detectedParts[0] && detectedParts[0] !== preFilledDetails.subAggregate) {
            setSubAggregateInputValue(detectedParts[0]);
            setVehicleDetails(prev => ({ ...prev, subAggregate: detectedParts[0] }));
          }
        } else if (preFilledDetails.subAggregate) {
          // Fallback to single subAggregate if no detectedParts
          setModalSelectedParts([preFilledDetails.subAggregate]);
        } else {
          setModalSelectedParts([]);
        }

        // Separate MMVFY and SubAggregate fields
        const mappedMissingMMVFY = missingMMVFY.map((field: string) => fieldMapping[field] || field.toLowerCase());
        const mappedMissingSubAggregate = missingSubAggregate.map((field: string) => fieldMapping[field] || field.toLowerCase());
        
        let cleanedSearchQuery = textToSearch.trim();
        
        // If vehicle number is detected, remove it from the search query display
        if (detectedVehicleNumber) {
          cleanedSearchQuery = removeVehicleNumberFromText(textToSearch.trim());
        }
        
        setMissingFieldsModal({
          isOpen: true,
          missingFields: mappedMissingFields,
          missingMMVFY: mappedMissingMMVFY,
          missingSubAggregate: mappedMissingSubAggregate,
          normalizedQuery: normalized,
          source: 'tvs',
          searchQuery: cleanedSearchQuery, // Use cleaned query without vehicle number
          correlationId: validationCorrelationId,
        });
        
        // Auto-detect and fill vehicle number from search query if present
        if (detectedVehicleNumber) {
          setVehicleNumber(detectedVehicleNumber);

          // Check if backend validation failed for this vehicle number
          if (vehicleValidationFailed) {
            // Vehicle number was detected but validation failed - show input field with error
            console.log(`[Modal Setup] Vehicle validation failed for ${detectedVehicleNumber}: ${vehicleValidationError}`);
            setVehicleNumberFromQuery(false); // Show the input field so user can edit
            setVehicleNumberError(vehicleValidationError || 'No vehicle found for this number. Please check and try again.');
            // Don't fetch vehicle details again since backend already validated
          } else {
            // Validation succeeded or not yet validated - hide input field and fetch details
            setVehicleNumberError('');
            setVehicleNumberFromQuery(true); // Mark that it came from query, so we hide the input field
            // Immediately fetch vehicle details if a valid vehicle number is detected (no debounce)
            // This ensures vehicle details are filled before showing modal
            fetchVehicleDetailsFromNumber(detectedVehicleNumber).then(() => {
              // After vehicle details are fetched, check if we still need to show the modal
              // If all MMVFY fields are filled and only part info is missing, modal will be updated
              // If all fields are filled, we could auto-close, but let's keep it open for user to verify
            }).catch((error) => {
              console.error('Error fetching vehicle details from detected number:', error);
            });
          }
        } else {
          // Reset vehicle number if not detected in query
          setVehicleNumber('');
          setVehicleNumberError('');
          setVehicleNumberFromQuery(false);
        }
        setVehicleDetailsFromNumber(false);
        setIsElectricVehicle(false);
        setVehicleClass('');
        
        // Fetch makes when modal opens
        if (vehicleMakes.length === 0) {
          fetchVehicleMakes();
        }
        
        // Fetch sub aggregates when modal opens
        fetchSubAggregates();
        
        // If make is pre-filled, fetch models
        // Pass skipVariantClear=true if model is also pre-filled to avoid race condition
        // where fetchVehicleModels clears variants after fetchVehicleVariants has populated them
        if (preFilledDetails.make) {
          const skipVariantClear = Boolean(preFilledDetails.model);
          fetchVehicleModels(preFilledDetails.make, skipVariantClear);
        }

        // If both make and model are pre-filled, fetch variants, fuel types, and years
        if (preFilledDetails.make && preFilledDetails.model) {
          console.log(`[Modal] Pre-filled make: "${preFilledDetails.make}", model: "${preFilledDetails.model}"`);
          console.log(`[Modal] Calling fetchVehicleVariants with make="${preFilledDetails.make}", model="${preFilledDetails.model}"`);
          fetchVehicleVariants(preFilledDetails.make, preFilledDetails.model);
          // Fetch years with whatever vehicle context is pre-filled
          fetchVehicleYears(
            preFilledDetails.make,
            preFilledDetails.model,
            preFilledDetails.variant || undefined,
            preFilledDetails.fuelType || undefined
          );
        } else {
          console.log(`[Modal] Not fetching variants - make: "${preFilledDetails.make}", model: "${preFilledDetails.model}"`);
        }
        
        const modalSetupDuration = performance.now() - modalSetupStartTime;
        const totalTime = performance.now() - validationStartTime;
        console.log(`[Timing] Modal setup took ${modalSetupDuration.toFixed(2)}ms`);
        console.log(`[Timing] ⏱️ TOTAL TIME from search click to modal open: ${totalTime.toFixed(2)}ms (${(totalTime / 1000).toFixed(2)}s)`);
        console.log(`[Timing] Breakdown: Validation=${validationDuration.toFixed(2)}ms, Modal Setup=${modalSetupDuration.toFixed(2)}ms`);
        
        setIsSearching(false);
        return;
      }
    }

    // Navigate to results page using URL params (robust for concurrent users)
    navigate(buildSearchUrl({ q: textToSearch.trim(), mode: computedMode }));
  };

  return (
    <div className="App min-h-screen bg-[#F5F5F7] flex items-center justify-center relative overflow-hidden">
      {/* Animated background gradient orbs - faster movement */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {/* Blue gradient orb - top left */}
        <div
          style={{
            position: 'absolute',
            top: '5%',
            left: '10%',
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(22, 119, 255, 0.12) 0%, transparent 70%)',
            filter: 'blur(80px)',
            animation: 'orbFloat 4s ease-in-out infinite',
          }}
        />
        {/* Purple gradient orb - bottom right */}
        <div
          style={{
            position: 'absolute',
            bottom: '10%',
            right: '5%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(114, 46, 209, 0.08) 0%, transparent 70%)',
            filter: 'blur(100px)',
            animation: 'orbFloat 5s ease-in-out infinite reverse',
          }}
        />
        {/* Green gradient orb - center */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '30%',
            width: 700,
            height: 700,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(82, 196, 26, 0.06) 0%, transparent 70%)',
            filter: 'blur(120px)',
            transform: 'translate(-50%, -50%)',
            animation: 'orbPulse 3s ease-in-out infinite',
          }}
        />
        {/* Orange gradient orb - top right */}
        <div
          style={{
            position: 'absolute',
            top: '15%',
            right: '15%',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 140, 0, 0.08) 0%, transparent 70%)',
            filter: 'blur(90px)',
            animation: 'orbDrift 6s ease-in-out infinite',
          }}
        />
        {/* Grid/checkbox pattern with movement */}
        <div
          style={{
            position: 'absolute',
            top: '-60px',
            left: '-60px',
            width: 'calc(100% + 120px)',
            height: 'calc(100% + 120px)',
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.05) 1.4px, transparent 1.4px),
              linear-gradient(90deg, rgba(0,0,0,0.05) 1.4px, transparent 1.4px)
            `,
            backgroundSize: '60px 60px',
            animation: 'gridMove 20s linear infinite',
          }}
        />
        {/* Subtle radial gradient overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at 70% 50%, rgba(255,255,255,0.6) 0%, transparent 70%)',
          }}
        />
      </div>
      <main className="w-full max-w-4xl px-4 sm:px-6 lg:px-10 py-4 sm:py-5 lg:py-6 relative z-10">
        <div className="w-full">
          <div className="bg-[#F3F3F5]/20 border border-gray-300 rounded-2xl shadow-sm p-6 sm:p-7 lg:p-8">
            <div className="text-center mb-5 sm:mb-6 lg:mb-7">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-900 mb-2 sm:mb-2.5">PartsGPT</h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed" style={{ lineHeight: '1.5' }}>
                Search across TVS Catalog, Boodmo Catalog, and AI-powered sources
              </p>
            </div>

            <div className="space-y-4 sm:space-y-5">
              {/* Unified Search Input */}
              <div 
                className="space-y-3"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="relative">
                  {/* Rotating Placeholder Overlay */}
                  {searchText.trim() === '' && (
                    <div 
                      className="absolute pointer-events-none pl-4 sm:pl-6 pr-4 sm:pr-6 pt-4 sm:pt-5 text-base sm:text-lg text-gray-400 overflow-hidden"
                      style={{ 
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        fontFamily: 'inherit',
                        fontSize: '1rem',
                        lineHeight: '1.5',
                        letterSpacing: 'normal',
                        fontWeight: 'normal',
                        pointerEvents: 'none',
                        boxSizing: 'border-box',
                      }}
                    >
                      <RotatingText
                        text={placeholderMessages}
                        duration={3500}
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                        className="text-gray-400"
                      />
                    </div>
                  )}
                  
                  {/* Inline Suggestion Overlay - Only shows completion part */}
                  {inlineSuggestion && searchText.length > 0 && (
                    <div 
                      className="absolute pointer-events-none pl-4 sm:pl-6 pr-4 sm:pr-6 pt-4 sm:pt-5 text-base sm:text-lg"
                      style={{ 
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: '3rem',
                        zIndex: 11,
                        fontFamily: 'inherit',
                        fontSize: '1rem',
                        lineHeight: '1.5',
                        letterSpacing: 'normal',
                        fontWeight: 'normal',
                        overflow: 'hidden',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        pointerEvents: 'none',
                        boxSizing: 'border-box',
                        margin: 0
                      }}
                    >
                      {/* Create invisible spacer for typed text to align properlyr */}
                      <span 
                        style={{ 
                          visibility: 'hidden',
                          fontFamily: 'inherit',
                          fontSize: '1rem',
                          lineHeight: '1.5',
                          letterSpacing: 'normal',
                          fontWeight: 'normal',
                          display: 'inline',
                          verticalAlign: 'baseline',
                          margin: 0,
                          padding: 0,
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          boxSizing: 'border-box',
                          height: 'auto',
                          textRendering: 'optimizeLegibility',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          fontFeatureSettings: 'normal',
                          fontVariant: 'normal'
                        }}
                      >
                        {searchText}
                      </span><span
                        style={{ 
                          color: 'rgb(17, 24, 39)',
                          fontFamily: 'inherit',
                          fontSize: '1rem',
                          lineHeight: '1.5',
                          letterSpacing: 'normal',
                          fontStyle: 'normal',
                          fontWeight: 'normal',
                          opacity: 1,
                          display: 'inline',
                          verticalAlign: 'text-bottom',
                          margin: 0,
                          padding: 0,
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          boxSizing: 'border-box',
                          height: 'auto',
                          textRendering: 'optimizeLegibility',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          fontFeatureSettings: 'normal',
                          fontVariant: 'normal',
                          position: 'relative',
                          top: '1px'
                        }}
                      >
                        {(() => {
                          // Normalize spaces to find the correct completion part
                          const normalizeSpaces = (str: string) => str.replace(/\s+/g, ' ').trim();
                          const normalizedSearchText = normalizeSpaces(searchText);
                          const normalizedSuggestion = normalizeSpaces(inlineSuggestion);
                          
                          // Find where the normalized searchText ends in the normalized suggestion
                          if (normalizedSuggestion.toLowerCase().startsWith(normalizedSearchText.toLowerCase())) {
                            // Find the completion part in normalized strings
                            const normalizedCompletion = normalizedSuggestion.substring(normalizedSearchText.length).trimStart();
                            
                            // Now find the corresponding position in the original inlineSuggestion
                            // by matching the normalized strings character by character
                            let normalizedSearchPos = 0;
                            let originalPos = 0;
                            
                            // Match up to the end of normalizedSearchText
                            while (normalizedSearchPos < normalizedSearchText.length && originalPos < inlineSuggestion.length) {
                              const normalizedChar = normalizedSearchText[normalizedSearchPos];
                              const originalChar = inlineSuggestion[originalPos];
                              
                              // Match characters (case-insensitive) or spaces
                              if (normalizedChar.toLowerCase() === originalChar.toLowerCase() || 
                                  (/\s/.test(normalizedChar) && /\s/.test(originalChar))) {
                                normalizedSearchPos++;
                                originalPos++;
                              } else if (/\s/.test(originalChar)) {
                                // Skip extra spaces in original
                                originalPos++;
                              } else {
                                // Character mismatch, break
                                break;
                              }
                            }
                            
                            // Get the completion part from original suggestion
                            // Preserve the space between make/model/variant/fuel, but skip extra spaces
                            let completionStart = originalPos;
                            // Only skip spaces if they're at the very start (after matching)
                            // But preserve the first space if it's part of the structure (make/model separation)
                            if (completionStart < inlineSuggestion.length && /\s/.test(inlineSuggestion[completionStart])) {
                              // Check if this space is part of the structure (between make/model)
                              // If the searchText ends with a word and suggestion continues with a word, preserve the space
                              const searchTextTrimmed = searchText.trim();
                              const lastCharIsSpace = searchTextTrimmed.length < searchText.length;
                              const nextCharIsLetter = completionStart + 1 < inlineSuggestion.length && 
                                                       /[a-zA-Z]/.test(inlineSuggestion[completionStart + 1]);
                              
                              // If searchText doesn't end with space and next char is a letter, preserve the space
                              if (!lastCharIsSpace && nextCharIsLetter) {
                                // Keep the space - it's part of the structure
                              } else {
                                // Skip extra spaces
                                while (completionStart < inlineSuggestion.length && /\s/.test(inlineSuggestion[completionStart])) {
                                  completionStart++;
                                }
                              }
                            }
                            const completion = inlineSuggestion.substring(completionStart);
                            // Remove any newlines or line breaks from completion, but preserve spaces
                            return completion.replace(/[\r\n]+/g, ' ').toLowerCase();
                          }
                          
                          // Fallback to simple substring with space normalization
                          let completionStart = searchText.length;
                          // Preserve the space between make/model/variant/fuel
                          // Only skip spaces if they're extra (multiple spaces)
                          if (completionStart < inlineSuggestion.length && /\s/.test(inlineSuggestion[completionStart])) {
                            const searchTextTrimmed = searchText.trim();
                            const lastCharIsSpace = searchTextTrimmed.length < searchText.length;
                            const nextCharIsLetter = completionStart + 1 < inlineSuggestion.length && 
                                                     /[a-zA-Z]/.test(inlineSuggestion[completionStart + 1]);
                            
                            // If searchText doesn't end with space and next char is a letter, preserve the space
                            if (!lastCharIsSpace && nextCharIsLetter) {
                              // Keep the space - it's part of the structure
                            } else {
                              // Skip extra spaces
                              while (completionStart < inlineSuggestion.length && /\s/.test(inlineSuggestion[completionStart])) {
                                completionStart++;
                              }
                            }
                          }
                          const completion = inlineSuggestion.substring(completionStart);
                          // Remove any newlines or line breaks from completion, but preserve spaces
                          return completion.replace(/[\r\n]+/g, ' ').toLowerCase();
                        })()}
                      </span>
                    </div>
                  )}
                  
                  <textarea
                    ref={textareaRef}
                    autoFocus
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      // Accept inline suggestion with Tab or Right Arrow
                      if ((e.key === 'Tab' || e.key === 'ArrowRight') && inlineSuggestion) {
                        e.preventDefault();
                        setSearchText(inlineSuggestion);
                        setInlineSuggestion('');
                        return;
                      }
                      // Handle dropdown navigation
                      if (suggestionsOpen && suggestions.length > 0) {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setSelectedSuggestionIndex(prev => 
                            prev < suggestions.length - 1 ? prev + 1 : prev
                          );
                          return;
                        }
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setSelectedSuggestionIndex(prev => 
                            prev > 0 ? prev - 1 : -1
                          );
                          return;
                        }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
                            setSearchText(suggestions[selectedSuggestionIndex]);
                            setSuggestionsOpen(false);
                            setSelectedSuggestionIndex(-1);
                            setInlineSuggestion('');
                            handleSearch();
                          } else {
                            handleSearch();
                          }
                          return;
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setSuggestionsOpen(false);
                          setSelectedSuggestionIndex(-1);
                          return;
                        }
                      }
                      // Handle Enter key: Send search if Shift is not pressed
                      if (e.key === 'Enter' && !e.shiftKey && !suggestionsOpen) {
                        e.preventDefault();
                        handleSearch();
                        return;
                      }
                    }}
                    placeholder={(() => {
                      // Use static placeholder (rotating text is handled by overlay)
                      if (searchText.trim() === '') {
                        return ' '; // Single space to maintain layout, actual text shown by RotatingText overlay
                      }
                      // Existing placeholder logic for when text exists or files are selected
                      return (searchText.trim() && selectedFiles.length>0) ? 'Describe and upload images to search...' : (selectedFiles.length>0) ? 'Upload an image to search for auto parts...' : 'How can I help you with Parts Search today?';
                    })()}
                    className="w-full min-h-[80px] sm:min-h-[96px] lg:min-h-[112px] bg-white/90 border border-gray-350 rounded-2xl pl-4 sm:pl-6 pr-4 sm:pr-6 pt-4 sm:pt-5 pb-12 sm:pb-14 text-base sm:text-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md resize-none relative z-10"
                    style={{ 
                      color: 'rgb(17, 24, 39)', // black text (gray-900)
                      caretColor: 'rgb(75, 85, 99)', // gray-600 for caret visibility
                      fontFamily: 'inherit',
                      fontSize: '1rem', // text-base
                      lineHeight: '1.5',
                      letterSpacing: 'normal',
                      boxSizing: 'border-box',
                      textRendering: 'optimizeLegibility',
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                      fontFeatureSettings: 'normal',
                      fontVariant: 'normal'
                    }}
                    disabled={isSearching}
                    rows={3}
                    onBlur={() => {
                      // Delay closing to allow click on suggestions
                      setTimeout(() => {
                        setSuggestionsOpen(false);
                        setSelectedSuggestionIndex(-1);
                      }, 200);
                    }}
                    onFocus={() => {
                      if (suggestions.length > 0) {
                        setSuggestionsOpen(true);
                      }
                    }}
                  />
                  
                  {/* Dropdown Suggestions - Amazon style */}
                  {suggestionsOpen && suggestions.length > 0 && (
                    <div 
                      className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto"
                      style={{
                        top: '100%',
                        left: 0,
                        right: 0,
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                      }}
                    >
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className={`px-4 py-3 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 transition-colors ${
                            selectedSuggestionIndex === index 
                              ? 'bg-blue-50 text-gray-900' 
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setSearchText(suggestion);
                            setSuggestionsOpen(false);
                            setSelectedSuggestionIndex(-1);
                            // Optionally trigger search automatically
                            // handleSearch();
                          }}
                          onMouseEnter={() => setSelectedSuggestionIndex(index)}
                        >
                          <div className="flex items-center">
                            <svg 
                              className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <span className="flex-1">{suggestion}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* How to Search Info Icon - Left side */}
                  <div className="absolute left-2 sm:left-3 md:left-4 bottom-2 sm:bottom-3 md:bottom-4" style={{ zIndex: 100 }}>
                    <div className="relative">
                      <button
                        type="button"
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 transition-all duration-200 text-gray-600 hover:text-gray-700"
                        onMouseEnter={() => setHoveredTooltip('search-info')}
                        onMouseLeave={() => setHoveredTooltip(null)}
                        onTouchStart={() => setHoveredTooltip(hoveredTooltip === 'search-info' ? null : 'search-info')}
                        aria-label="How to search"
                      >
                        <InfoIcon size={14} className="w-3.5 h-3.5" />
                      </button>
                      {hoveredTooltip === 'search-info' && (
                        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 bg-gray-900 text-white rounded-lg shadow-xl"
                          style={{
                            width: 'max-content',
                            minWidth: '400px',
                            maxWidth: 'min(500px, calc(100vw - 2.5rem))',
                            zIndex: 9999,
                          }}
                        >
                          <div className="px-4 sm:px-5 py-3 sm:py-4 space-y-3 sm:space-y-4">
                            <div>
                              <div className="font-semibold text-white mb-1.5 text-xs sm:text-sm">By Vehicle Number:</div>
                              <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-1">
                                Enter Indian vehicle number (e.g., TN10AB1234, 22BH1234AB). We automatically extract Make, Model, Variant, Fuel Type, and Year to show all compatible parts.
                              </div>
                              <div className="text-gray-400 text-xs italic">Tip: Include part name for specific results (e.g., "TN10AB1234 brake pad")</div>
                            </div>
                            
                            <div>
                              <div className="font-semibold text-white mb-1.5 text-xs sm:text-sm">By Part Number / Name:</div>
                              <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-1">
                                Enter part number (e.g., "ABC123") or part name (e.g., "brake pad"). For best results, include vehicle details: Make, Model, Variant, Fuel Type, and Year.
                              </div>
                              <div className="text-gray-400 text-xs italic">Example: "brake pad for Ford Ecosport Trend Diesel 2019"</div>
                            </div>
                            
                            <div>
                              <div className="font-semibold text-white mb-1.5 text-xs sm:text-sm">By Image:</div>
                              <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-1">
                                Upload a clear photo of the part. We identify the part and show matching vehicles. Add text description for better accuracy.
                              </div>
                              <div className="text-gray-400 text-xs italic">Best: Upload image + describe vehicle (e.g., "for Hyundai i20 2016")</div>
                            </div>
                            
                            <div>
                              <div className="font-semibold text-white mb-1.5 text-xs sm:text-sm">By Voice:</div>
                              <div className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-1">
                                Speak naturally describing the part and vehicle. Include: part name, make, model, variant, fuel type, and year if known.
                              </div>
                              <div className="text-gray-400 text-xs italic">Example: "Clutch plate for 2016 Hyundai i20 diesel Sportz"</div>
                            </div>
                            
                            <div className="pt-2 border-t border-gray-700">
                              <div className="font-semibold text-white mb-1.5 text-xs sm:text-sm">For Best Results:</div>
                              <div className="text-gray-300 text-xs sm:text-sm leading-relaxed space-y-1">
                                <div>• Include complete vehicle details: Make, Model, Variant, Fuel Type, Year</div>
                                <div>• Be specific: "brake pad" is better than "brake"</div>
                                <div>• Vehicle number automatically fills all vehicle details</div>
                                <div>• Part numbers can auto-detect vehicle compatibility</div>
                              </div>
                            </div>
                          </div>
                          <div className="absolute right-full top-1/2 -translate-y-1/2 -mr-1 border-4 border-transparent border-r-gray-900"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons Row - Inside textarea bottom right */}
                  <div className="absolute right-2 sm:right-3 md:right-4 bottom-2 sm:bottom-3 md:bottom-4 flex items-center gap-1 sm:gap-2 z-10 flex-wrap max-w-[calc(100%-1rem)] justify-end">
                    {(searchText.trim().length > 0 || selectedFiles.length > 0) && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchText('');
                          setSelectedFiles([]);
                          setSelectedPreviews([]);
                          try { fileInputRef.current && (fileInputRef.current.value = ''); } catch {}
                        }}
                        className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 sm:py-1.5 bg-white/40 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50/50 transition-all duration-200 text-[10px] sm:text-xs md:text-sm font-medium shrink-0"
                        title="Clear"
                        aria-label="Clear input"
                      >
                        <ClearIcon className="w-3.5 h-3.5 text-gray-600" />
                        <span>Clear</span>
                      </button>
                    )}
                    
                    <button
                      type="button"
                      onClick={handleImageUpload}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/40 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50/50 transition-all duration-200 text-xs sm:text-sm font-medium"
                      title="Attach images"
                      aria-label="Attach images"
                    >
                      <CameraIcon size={14} className="w-3.5 h-3.5 text-gray-600" />
                      <span>Attach</span>
                    </button>

                    {!(selectedFiles.length > 0 && !searchText.trim()) && (
                      <button
                        type="button"
                        onClick={toggleRecording}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 bg-white/40 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50/50 transition-all duration-200 text-xs sm:text-sm font-medium ${
                          isRecording ? 'voice-recording-active' : ''
                        }`}
                        title={isRecording ? 'Stop recording' : 'Voice search'}
                        aria-label="Voice search"
                      >
                        {isRecording ? (
                          <span className="relative flex items-center justify-center voice-recording-wrapper">
                            {/* Pulsing rings */}
                            <span className="absolute inline-flex h-full w-full rounded-full voice-pulse-ring voice-pulse-ring-1" />
                            <span className="absolute inline-flex h-full w-full rounded-full voice-pulse-ring voice-pulse-ring-2" />
                            <span className="absolute inline-flex h-full w-full rounded-full voice-pulse-ring voice-pulse-ring-3" />
                            {/* Glowing background */}
                            <span className="absolute inline-flex h-full w-full rounded-full voice-glow-bg" />
                            {/* Icon */}
                            <span className="relative inline-flex voice-icon-pulse">
                              <MicIcon size={14} className="w-3.5 h-3.5 text-red-500 voice-icon-glow" />
                            </span>
                          </span>
                        ) : (
                          <MicIcon2 size={14} className="w-3.5 h-3.5 text-gray-600" />
                        )}
                        <span>Voice</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handleSearch()}
                      disabled={isSearching || (!searchText.trim() && selectedFiles.length === 0)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 bg-white/40 border border-gray-200 rounded-lg text-gray-700 transition-all duration-200 text-xs sm:text-sm font-medium ${
                        isSearching || (!searchText.trim() && selectedFiles.length === 0)
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:bg-gray-50/50 cursor-pointer'
                      }`}
                      title={isSearching ? 'Searching...' : (!searchText.trim() && selectedFiles.length === 0) ? 'Enter text or upload images to search' : 'Send'}
                      aria-label="Send"
                    >
                      {isSearching ? (
                        <LoadingSpinner />
                      ) : (
                        <>
                          <SendIcon className="w-3.5 h-3.5 text-gray-600" />
                          <span>Send</span>
                        </>
                      )}
                    </button>
                  </div>

                </div>

                {/* Recent Searches */}
                {searchText.trim().length === 0 && recentSearches.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2.5 pt-2">
                    {recentSearches.slice(0, 5).map((s, i) => (
                      <button
                        key={`${s}-${i}`}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setSearchText(s);
                          try {
                            if (textareaRef.current) {
                              textareaRef.current.focus();
                              // Set cursor to end
                              setTimeout(() => {
                                try {
                                  if (textareaRef.current) {
                                    textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
                                  }
                                } catch (err) {
                                  // Ignore selection errors
                                }
                              }, 0);
                            }
                          } catch (err) {
                            // Ignore focus errors
                          }
                        }}
                        className="px-3 py-1.5 text-xs sm:text-sm bg-white/40 border border-gray-200 rounded-full hover:bg-gray-100/50 hover:border-gray-300 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow cursor-pointer"
                        aria-label={`Recent ${s}`}
                      >
                        {s}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50/50 rounded-full transition-colors bg-white/40 cursor-pointer"
                      onClick={(e) => { 
                        e.preventDefault(); 
                        setRecentSearches([]); 
                        try { localStorage.removeItem('recent_searches'); } catch {} 
                      }}
                      title="Clear recent searches"
                      aria-label="Clear recent searches"
                    >
                      Clear
                    </button>
                  </div>
                )}

                {/* Part Number Intent Badge */}
                {partNumberIntent && (
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1.5 text-xs rounded-full border border-gray-300 bg-gray-100/50 text-gray-800 font-medium">
                      Part number detected: {partNumberIntent.partNumber}
                    </span>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs bg-white/40 border border-gray-200 rounded-full hover:bg-gray-50/50 transition-colors"
                      onClick={() => setSearchText(partNumberIntent.partNumber)}
                    >
                      Use part number
                    </button>
                  </div>
                )}

                {/* Selected Images Thumbnails */}
                {selectedFiles.length > 0 && (
                  <div className="flex items-center gap-3 p-3 sm:p-4 bg-white/40 border border-gray-200 rounded-2xl shadow-sm hover:shadow transition-shadow">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <ImageIcon className="w-5 h-5 text-gray-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700 truncate">
                        {selectedFiles.length} image{selectedFiles.length > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5 flex-shrink-0">
                      <div className="flex -space-x-2">
                        {selectedFiles.slice(0, 3).map((file, index) => (
                          <div key={index} className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden border-2 border-white shadow-sm hover:scale-110 transition-transform">
                            {selectedPreviews[index] ? (
                              <img src={selectedPreviews[index]} alt={file.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gray-100/50 flex items-center justify-center">
                                <ImageIcon className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                          </div>
                        ))}
                        {selectedFiles.length > 3 && (
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gray-100/50 border-2 border-white flex items-center justify-center shadow-sm">
                            <span className="text-xs font-medium text-gray-600">+{selectedFiles.length - 3}</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleImageUpload}
                        className="px-3 py-1.5 text-xs bg-white/40 border border-gray-200 rounded-lg hover:bg-gray-50/50 hover:border-gray-300 transition-colors font-medium whitespace-nowrap"
                      >
                        Change
                      </button>
                      <button
                        onClick={() => {
                          setSelectedFiles([]);
                          setSelectedPreviews([]);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50/50 rounded-lg transition-colors"
                        title="Remove all images"
                        aria-label="Remove all images"
                      >
                        <ClearIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* NLP Query Suggestions - Try with these */}
                <p className="text-sm font-medium text-gray-600 pt-4 sm:pt-5 mb-4">
                  Try with one of these
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {[
                    'Find brake pad for Maruti Swift 2020',
                    'Air filter for Tata Nexon 2019?',
                    'Oil filter for Mahindra XUV500 2021?',
                    'Options for spark plug, Hyundai i20 2018',
                    'Need headlight for Honda City 2022',
                    'Brake Pad, Air Filter, Oil Filter for Ford Ecosport 2015'
                  ].map((query, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setSearchText(query);
                        try {
                          if (textareaRef.current) {
                            textareaRef.current.focus();
                            setTimeout(() => {
                              try {
                                if (textareaRef.current) {
                                  textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
                                }
                              } catch (err) {
                                // Ignore selection errors
                              }
                            }, 0);
                          }
                        } catch (err) {
                          // Ignore focus errors
                        }
                      }}
                      className="px-4 py-2.5 text-sm bg-white/40 border border-gray-200 rounded-xl hover:bg-gray-50/50 hover:border-gray-300 hover:shadow-sm transition-all duration-200 font-medium text-gray-600 cursor-pointer"
                      aria-label={`Search: ${query}`}
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                aria-label="Upload images for search"
                title="Select images to upload for search"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 bg-red-50/50 border border-red-200 rounded-2xl shadow-sm p-4 sm:p-5">
                <div className="flex items-center gap-3 sm:gap-4">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm sm:text-base text-red-700 font-medium" style={{ lineHeight: '1.6' }}>{error}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Missing Fields Modal */}
      {missingFieldsModal.isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
          onClick={handleCloseModal}
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        >
          <div 
            className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-slideUp ${
              missingFieldsModal.missingSubAggregate.length > 0 && missingFieldsModal.missingMMVFY.length > 0
                ? 'max-w-6xl' // Two sections: wider modal
                : 'max-w-3xl' // Single section: narrower modal
            }`}
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            {/* Header */}
            <div className="bg-black px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Additional Information Required</h3>
                  <p className="text-gray-300 text-sm mt-0.5">Please provide the missing vehicle details</p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {/* Search Query Display */}
              {(missingFieldsModal.searchQuery || selectedFiles.length > 0) && (
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-2">Search Query:</p>
                  <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                    {selectedFiles.length > 0 && !missingFieldsModal.searchQuery ? (
                      <p className="text-sm text-gray-600 italic">Image search - parts will be detected from uploaded images</p>
                    ) : (
                      <p className="text-sm text-gray-800 font-medium">{missingFieldsModal.searchQuery}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Form */}
              <form onSubmit={handleVehicleDetailsSubmit} className="space-y-6">
                {/* Sections Container - Horizontal Layout */}
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Section 1: Part Information */}
                  {missingFieldsModal.missingSubAggregate.length > 0 && (() => {
                    const isPartInfoComplete = modalSelectedParts.length > 0 || partNumberInputValue.trim().length > 0 || selectedFiles.length > 0;
                    // Disable image upload only if:
                    // 1. Parts are manually selected (parts > 0 but no files uploaded) - user chose manual entry
                    // 2. Part number is entered (mutually exclusive with images)
                    // Allow more uploads if files already exist (parts came from image detection)
                    const isImageUploadDisabled = (modalSelectedParts.length > 0 && selectedFiles.length === 0) || partNumberInputValue.trim().length > 0;
                    // Part Name is never disabled - it shows loading indicator during detection and allows editing
                    const isPartNameDisabled = false;
                    return (
                    <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-6 flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-4">
                      {/* Step 1 Indicator */}
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        isPartInfoComplete
                          ? 'bg-green-500 text-white'
                          : 'bg-black text-white'
                      }`}>
                        {isPartInfoComplete ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          '1'
                        )}
                      </div>
                      <h4 className="text-xl font-bold text-gray-800">Part Information</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">Please provide part name, part number, or upload a part image</p>

                    {/* Part Name - Multi-select */}
                    <div className={`mb-4 ${isPartNameDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
                      <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-2">
                        Part Name
                        <span className="text-xs text-gray-500 font-normal">(Select up to 5 parts)</span>
                        {!isPartNameDisabled && selectedFiles.length === 0 && !partNumberInputValue.trim() && modalSelectedParts.length === 0 && (
                          <span className="text-red-500 text-xs font-bold">*</span>
                        )}
                      </label>
                      <MultiPartSelector
                        selectedParts={modalSelectedParts}
                        onPartsChange={(parts) => {
                          setModalSelectedParts(parts);
                          // Clear validation error when parts change
                          setPartValidationError(null);
                          setPartValidationSuggestions([]);
                          // Also sync first part to subAggregate for backward compatibility
                          if (parts.length > 0) {
                            setSubAggregateInputValue(parts[0]);
                            setVehicleDetails(prev => ({ ...prev, subAggregate: parts[0] }));
                          } else {
                            setSubAggregateInputValue('');
                            setVehicleDetails(prev => ({ ...prev, subAggregate: '' }));
                          }
                        }}
                        maxParts={5}
                        placeholder="Search and select parts (e.g., BRAKE PAD, CLUTCH PLATE)"
                        disabled={isPartNameDisabled}
                        source="tvs"
                        isDetecting={isDetectingParts}
                        onValidationError={(error, suggestions) => {
                          setPartValidationError(error);
                          setPartValidationSuggestions(suggestions);
                        }}
                      />
                      {/* Show detected parts summary (only after detection completes) */}
                      {selectedFiles.length > 0 && modalSelectedParts.length > 0 && !isDetectingParts && (
                        <span className="text-xs text-green-600 flex items-center gap-1.5 font-medium mt-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {modalSelectedParts.length} part(s) selected (see details under each image below)
                        </span>
                      )}
                      {/* Show detected part from query when no parts are selected yet */}
                      {missingFieldsModal.normalizedQuery?.subAggregate && modalSelectedParts.length === 0 && selectedFiles.length === 0 && (
                        <span className="text-xs text-blue-600 flex items-center gap-1.5 font-medium mt-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Detected: {missingFieldsModal.normalizedQuery.subAggregate}
                        </span>
                      )}
                      {/* Show part validation error from modal-level state */}
                      {partValidationError && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm text-red-700">{partValidationError}</p>
                              {partValidationSuggestions.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-red-600 mb-1">Try one of these instead:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {partValidationSuggestions.slice(0, 5).map((suggestion) => (
                                      <button
                                        key={suggestion}
                                        type="button"
                                        onClick={() => {
                                          setPartValidationError(null);
                                          setPartValidationSuggestions([]);
                                          setModalSelectedParts([...modalSelectedParts, suggestion]);
                                          setSubAggregateInputValue(suggestion);
                                          setVehicleDetails(prev => ({ ...prev, subAggregate: suggestion }));
                                        }}
                                        className="px-2 py-1 bg-white border border-red-300 rounded text-xs text-red-700 hover:bg-red-100 transition-colors"
                                      >
                                        {suggestion}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* OR Divider between Part Name and Part Number */}
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="text-xs font-medium text-gray-500 uppercase">OR</span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>

                    {/* Part Number Input - Below Part Name */}
                    <div className={`mb-4 ${isPartNameDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
                      <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-2">
                        Part Number
                        <span className="text-xs text-gray-500 font-normal">(use comma for multiple)</span>
                        {!isPartNameDisabled && modalSelectedParts.length === 0 && selectedFiles.length === 0 && !partNumberInputValue.trim() && (
                          <span className="text-red-500 text-xs font-bold">*</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={partNumberInputValue}
                        onChange={(e) => handlePartNumberInputChange(e.target.value.toUpperCase())}
                        placeholder="e.g., 13780M68K00, MSI-D1033, 26296AG000"
                        className="w-full border-2 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white uppercase"
                        disabled={isPartNameDisabled}
                      />
                      <span className="text-xs text-gray-500 mt-1 block">
                        Separate multiple part numbers with commas for multi-search
                      </span>
                      {missingFieldsModal.normalizedQuery?.partNumber && (
                        <span className="text-xs text-blue-600 flex items-center gap-1.5 font-medium mt-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Detected: {missingFieldsModal.normalizedQuery.partNumber}
                        </span>
                      )}
                    </div>

                    {/* OR Divider */}
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="text-xs font-medium text-gray-500 uppercase">OR</span>
                      <div className="flex-1 border-t border-gray-300"></div>
              </div>

                    {/* Image Upload Option */}
                    <div className={isImageUploadDisabled ? 'opacity-50 pointer-events-none' : ''}>
                      <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-2">
                        Upload Part Image
                        <span className="text-xs text-gray-500 font-normal">(up to 5 images)</span>
                        {selectedFiles.length === 0 && !isImageUploadDisabled && <span className="text-red-500 text-xs font-bold">*</span>}
                      </label>

                      {/* Thumbnail Grid with Per-Image Detected Parts */}
                      {selectedFiles.length > 0 && (
                        <div className="space-y-3 mb-3">
                          {selectedPreviews.map((preview, index) => {
                            const hasError = imageDetectionErrors.has(index);
                            const errorMessage = imageDetectionErrors.get(index);
                            const imageParts = perImageParts.get(index) || [];
                            return (
                            <div key={index} className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
                              {/* Image Thumbnail */}
                              <div className="relative flex-shrink-0">
                                <img
                                  src={preview}
                                  alt={`Preview ${index + 1}`}
                                  className={`w-16 h-16 object-cover rounded-lg border-2 ${
                                    hasError ? 'border-red-400' : detectingImageIndices.has(index) ? 'border-blue-300' : 'border-gray-300'
                                  }`}
                                />
                                {/* Loading indicator per image - only show for THIS specific image */}
                                {detectingImageIndices.has(index) && !hasError && (
                                  <div className="absolute inset-0 bg-white/60 rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  </div>
                                )}
                                {/* Error indicator per image */}
                                {hasError && (
                                  <div className="absolute inset-0 bg-red-100/80 rounded-lg flex items-center justify-center" title={errorMessage}>
                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    URL.revokeObjectURL(preview);
                                    const newFiles = selectedFiles.filter((_, i) => i !== index);
                                    const newPreviews = selectedPreviews.filter((_, i) => i !== index);
                                    setSelectedFiles(newFiles);
                                    setSelectedPreviews(newPreviews);

                                    // Update detectingImageIndices - remove this index and re-index remaining
                                    setDetectingImageIndices(prev => {
                                      const updated = new Set<number>();
                                      prev.forEach(idx => {
                                        if (idx < index) updated.add(idx);
                                        else if (idx > index) updated.add(idx - 1);
                                        // Skip idx === index (the removed image)
                                      });
                                      return updated;
                                    });

                                    // Update error and parts indices
                                    if (imageDetectionErrors.size > 0 || perImageParts.size > 0) {
                                      const newErrors = new Map<number, string>();
                                      const newParts = new Map<number, string[]>();
                                      imageDetectionErrors.forEach((err, idx) => {
                                        if (idx < index) newErrors.set(idx, err);
                                        else if (idx > index) newErrors.set(idx - 1, err);
                                      });
                                      perImageParts.forEach((parts, idx) => {
                                        if (idx < index) newParts.set(idx, parts);
                                        else if (idx > index) newParts.set(idx - 1, parts);
                                      });
                                      setImageDetectionErrors(newErrors);
                                      setPerImageParts(newParts);
                                      // Update modalSelectedParts to remove parts from deleted image
                                      const removedParts = perImageParts.get(index) || [];
                                      if (removedParts.length > 0) {
                                        const remainingParts = modalSelectedParts.filter(p => !removedParts.includes(p));
                                        setModalSelectedParts(remainingParts);
                                      }
                                    }
                                  }}
                                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md transition-colors"
                                  disabled={isImageUploadDisabled}
                                >
                                  &times;
                                </button>
                              </div>

                              {/* Detected Parts for this Image */}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-500 mb-1">Image {index + 1}</p>
                                {detectingImageIndices.has(index) && !hasError ? (
                                  <p className="text-xs text-blue-600">Detecting...</p>
                                ) : hasError ? (
                                  <p className="text-xs text-red-500">{errorMessage || 'Detection failed'}</p>
                                ) : imageParts.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {imageParts.map((part, partIdx) => (
                                      <span
                                        key={partIdx}
                                        className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full"
                                      >
                                        {part}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400">No parts detected</p>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Error summary for failed image detections */}
                      {imageDetectionErrors.size > 0 && (
                        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-xs text-red-600 flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            {imageDetectionErrors.size === selectedFiles.length
                              ? 'Could not detect parts from images. Please enter part name manually.'
                              : `Detection failed for ${imageDetectionErrors.size} image(s). Other parts were detected successfully.`
                            }
                          </p>
                        </div>
                      )}

                      {/* Upload Area */}
                      <div
                        onClick={() => !isImageUploadDisabled && selectedFiles.length < 5 && fileInputRef.current?.click()}
                        className={`border-2 border-dashed border-gray-300 rounded-xl p-4 text-center transition-all duration-200 ${
                          isImageUploadDisabled || selectedFiles.length >= 5
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer hover:border-blue-400 hover:bg-blue-50/30'
                        }`}
                      >
                        {selectedFiles.length > 0 ? (
                          <div className="flex items-center justify-center gap-2">
                            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <p className="text-sm text-gray-600">
                              {selectedFiles.length >= 5 ? 'Maximum 5 images reached' : 'Click to add more images'}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <svg className="w-8 h-8 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-sm text-gray-700 font-medium">Click to upload part images</p>
                            <p className="text-xs text-gray-500">Supports JPG, PNG, GIF (max 5 images)</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                    );
                  })()}

                  {/* Section 2: Vehicle Details */}
                  {missingFieldsModal.missingMMVFY.length > 0 && (() => {
                    const isPartInfoComplete = modalSelectedParts.length > 0 || partNumberInputValue.trim().length > 0 || selectedFiles.length > 0;
                    // Vehicle Details is complete if either all required MMVFY fields are filled OR vehicle number is provided
                    // Check if all required MMVFY fields are actually filled
                    // Variant is always required if any MMVFY field is missing
                    const variantRequired = missingFieldsModal.missingMMVFY.length > 0;
                    const variantFilled = !variantRequired || vehicleDetails.variant.trim().length > 0;
                    
                    // Check each missing MMVFY field
                    const allMissingFieldsFilled = missingFieldsModal.missingMMVFY.every(field => {
                      const fieldLower = field.toLowerCase();
                      // If make is missing, both make and model are required (they go together)
                      if (fieldLower === 'make') {
                        return vehicleDetails.make.trim().length > 0 && vehicleDetails.model.trim().length > 0;
                      }
                      if (fieldLower === 'model') {
                        return vehicleDetails.model.trim().length > 0;
                      }
                      if (fieldLower === 'variant') {
                        return vehicleDetails.variant.trim().length > 0;
                      }
                      if (fieldLower === 'fueltype' || fieldLower === 'fuel type') {
                        return vehicleDetails.fuelType.trim().length > 0;
                      }
                      if (fieldLower === 'year') {
                        return vehicleDetails.year.trim().length === 4 && /^\d{4}$/.test(vehicleDetails.year.trim());
                      }
                      return true;
                    });
                    
                    const allRequiredMMVFYFilled = variantFilled && allMissingFieldsFilled;
                    const isVehicleInfoComplete = allRequiredMMVFYFilled || vehicleNumber.trim().length > 0;
                    const isSection2Disabled = !isPartInfoComplete;
                    // Disable vehicle number input only if all MMVFY fields are filled manually (not from vehicle number)
                    // If details were fetched from vehicle number, keep the input enabled so user can change it
                    const isVehicleNumberDisabled = allRequiredMMVFYFilled && !vehicleDetailsFromNumber;
                    return (
                    <div className={`bg-blue-50/50 border border-blue-200 rounded-xl p-6 flex-1 min-w-0 ${isSection2Disabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2 mb-4">
                      {/* Step 2 Indicator */}
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        isVehicleInfoComplete 
                          ? 'bg-green-500 text-white' 
                          : isSection2Disabled
                          ? 'bg-gray-400 text-white'
                          : 'bg-black text-white'
                      }`}>
                        {isVehicleInfoComplete ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          '2'
                        )}
                      </div>
                      <h4 className="text-xl font-bold text-gray-800">Vehicle Details</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">Please provide vehicle details by  entering vehicle number or selecting from dropdowns</p>
                    {isFetchingVehicleDetails && (
                      <div className="flex items-center justify-center gap-2 mb-4 text-sm text-blue-600">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Fetching vehicle details...</span>
                      </div>
                    )}
                    
                    {/* Vehicle Number Input Option - Only show if NOT detected from query */}
                    {!vehicleNumberFromQuery && (
                      <>
                        <div className="mb-4">
                          <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-3">
                            Vehicle Number (Auto-fills all details)
                          </label>
                          
                          {/* License Plate Input - Boodmo Style (Direct Entry) */}
                          <div className="relative mb-4 flex justify-center">
                            <div 
                              className="relative rounded-lg overflow-hidden"
                              style={{ 
                                width: '100%',
                                maxWidth: '320px',
                                height: '70px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.32), 0 2px 4px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.1)',
                                margin: '0 auto',
                                backgroundColor: (() => {
                                  // Electric vehicles get green (including LMV + Electric)
                                  if (isElectricVehicle) {
                                    return '#10B981';
                                  }
                                  // LMV without electric gets white
                                  if (vehicleClass === 'LMV') {
                                    return '#FFFFFF';
                                  }
                                  // Other vehicle classes get yellow
                                  if (vehicleClass) {
                                    return '#FFD700';
                                  }
                                  // Default is white
                                  return '#FFFFFF';
                                })(), // Green for electric (including LMV+Electric), white for LMV without electric, yellow for other vehicle classes, white as default
                                border: '1.5px solid #000000',
                                outline: '1.5px solid #FFFFFF',
                                outlineOffset: '1px',
                                opacity: isVehicleNumberDisabled ? 0.5 : 1,
                                filter: isVehicleNumberDisabled ? 'blur(2px)' : 'none',
                                transition: 'opacity 0.3s ease, filter 0.3s ease',
                                pointerEvents: isVehicleNumberDisabled ? 'none' : 'auto'
                              }}
                            >
                              {/* License Plate Logo - Top Left Corner */}
                              <div 
                                className="absolute top-2 left-2 z-10"
                                style={{ width: '30px', height: '30px' }}
                              >
                                <img 
                                  src="/images/platelogo.png" 
                                  alt="License Plate Logo" 
                                  className="w-full h-full object-contain"
                                  style={{ 
                                    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                                    pointerEvents: 'none'
                                  }}
                                  onError={(e) => {
                                    // Fallback if image doesn't load
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </div>
                              
                              {/* Direct Input Field Inside License Plate */}
                              <input
                                type="text"
                                value={vehicleNumber}
                                onChange={(e) => handleVehicleNumberChange(e.target.value)}
                                placeholder="TN01AZ2345"
                                disabled={isFetchingVehicleDetails || isSection2Disabled || isVehicleNumberDisabled}
                                maxLength={15}
                                className="absolute inset-0 w-full h-full bg-transparent border-0 outline-none text-center font-bold"
                                 style={{ 
                                   fontSize: '32px',
                                   letterSpacing: '6px',
                                   color: vehicleNumber.trim() ? '#4B5563' : '#9CA3AF',
                                   fontFamily: 'Arial, "Helvetica Neue", sans-serif',
                                   textShadow: '0 1px 2px rgba(0, 0, 0, 0.21), 0 -1px 1px rgba(255,255,255,0.8)',
                                   fontWeight: '700',
                                   textTransform: 'uppercase',
                                   paddingLeft: '40px',
                                   paddingRight: '20px',
                                   paddingTop: '4px',
                                   paddingBottom: '4px',
                                   lineHeight: '1',
                                   transform: 'scaleY(1.40)',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}
                              />
                            </div>
                            {/* Validation Error Display */}
                            {vehicleNumberError && vehicleNumber.trim() && (
                              <p className="text-xs text-red-600 mt-2 text-center font-medium">{vehicleNumberError}</p>
                            )}
                          </div>

                          {/* Fetched Vehicle Details Display - Show below vehicle number when details are fetched */}
                          {vehicleDetailsFromNumber && !isFetchingVehicleDetails && (vehicleDetails.make || vehicleDetails.model) && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-2">
                                <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-green-800">
                                  <span className="font-semibold">Vehicle Found: </span>
                                  {[
                                    vehicleDetails.make,
                                    vehicleDetails.model,
                                    vehicleDetails.variant,
                                    vehicleDetails.fuelType,
                                    vehicleDetails.year
                                  ].filter(Boolean).join(' | ')}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* OR Divider - Always visible when vehicle number input is visible */}
                        <div className="flex items-center gap-3 my-4">
                          <div className="flex-1 border-t border-gray-300"></div>
                          <span className="text-xs font-medium text-gray-500 uppercase">OR</span>
                          <div className="flex-1 border-t border-gray-300"></div>
                        </div>
                      </>
                    )}
                    
                    {/* Show message if vehicle number was detected from query */}
                    {vehicleNumberFromQuery && vehicleNumber && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-blue-800 font-medium">
                            Vehicle number <span className="font-bold">{vehicleNumber}</span> detected from your search query and will be used automatically.
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Vehicle Details Dropdowns - Always visible, disabled when details fetched from vehicle number */}
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${vehicleDetailsFromNumber ? 'opacity-60' : ''}`}>
                  {/* Make Field */}
                      {missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'make') && (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        Make
                        <span className="text-red-500 text-xs font-bold">*</span>
                      </label>
                      <select
                        value={vehicleDetails.make}
                        onChange={(e) => handleVehicleDetailChange('make', e.target.value)}
                        className="border-2 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        required
                        disabled={isSection2Disabled || Boolean(vehicleDetailsFromNumber && vehicleDetails.make) || (loadingVehicleData && vehicleMakes.length === 0)}
                        title={vehicleDetailsFromNumber && vehicleDetails.make ? "Disabled: Vehicle details fetched from vehicle number" : ""}
                      >
                        <option value="">{loadingVehicleData && vehicleMakes.length === 0 ? 'Loading...' : 'Select Make'}</option>
                        {vehicleMakes.map((make) => (
                          <option key={make} value={make}>{make}</option>
                        ))}
                      </select>
                      {missingFieldsModal.normalizedQuery?.make && (
                        <span className="text-xs text-blue-600 flex items-center gap-1.5 font-medium">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Detected value: {missingFieldsModal.normalizedQuery.make}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Model Field - Show if model is missing OR if make is missing (they go together) */}
                      {(missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'model') || missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'make')) && (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        Model
                        <span className="text-red-500 text-xs font-bold">*</span>
                      </label>
                      <select
                        value={vehicleDetails.model}
                        onChange={(e) => handleVehicleDetailChange('model', e.target.value)}
                        disabled={isSection2Disabled || !vehicleDetails.make || (loadingVehicleData && vehicleModels.length === 0) || Boolean(vehicleDetailsFromNumber && vehicleDetails.model)}
                        className="border-2 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        required={missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'model') || missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'make')}
                        title={
                          vehicleDetailsFromNumber && vehicleDetails.model 
                            ? "Disabled: Vehicle details fetched from vehicle number" 
                            : !vehicleDetails.make 
                            ? "Please select Make first"
                            : loadingVehicleData
                            ? "Loading models..."
                            : vehicleModels.length === 0 && vehicleDetails.make
                            ? "No models found for this make"
                            : ""
                        }
                      >
                        <option value="">
                          {!vehicleDetails.make 
                            ? 'Select Model (choose Make first)' 
                            : (loadingVehicleData && vehicleModels.length === 0 ? 'Loading...' : 'Select Model')}
                        </option>
                        {vehicleModels.map((model) => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                      {missingFieldsModal.normalizedQuery?.model && (
                        <span className="text-xs text-blue-600 flex items-center gap-1.5 font-medium">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Detected value: {missingFieldsModal.normalizedQuery.model}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Variant Field - Always Required - Show if any MMVFY field is missing (variant is always required) OR if variant is already filled (to allow editing) */}
                      {((missingFieldsModal.missingMMVFY.length > 0) ||
                        (vehicleDetails.variant && missingFieldsModal.isOpen)) && (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        Variant
                        <span className="text-red-500 text-xs font-bold">*</span>
                      </label>
                      <select
                        value={vehicleDetails.variant}
                        onChange={(e) => handleVehicleDetailChange('variant', e.target.value)}
                        disabled={isSection2Disabled || !vehicleDetails.model || (loadingVehicleData && vehicleVariants.length === 0) || Boolean(vehicleDetailsFromNumber && vehicleDetails.variant)}
                        className="border-2 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        required
                        title={
                          vehicleDetailsFromNumber && vehicleDetails.variant 
                            ? "Disabled: Vehicle details fetched from vehicle number" 
                            : !vehicleDetails.model 
                            ? "Please select Model first"
                            : ""
                        }
                      >
                        <option value="">
                          {!vehicleDetails.model 
                            ? 'Select Variant (choose Model first)' 
                            : (loadingVehicleData && vehicleVariants.length === 0 ? 'Loading...' : `Select Variant (${vehicleVariants.length} available)`)}
                        </option>
                        {vehicleVariants.length > 0 ? (
                          vehicleVariants.map((variant) => (
                            <option key={variant} value={variant}>{variant}</option>
                          ))
                        ) : (
                          !loadingVehicleData && vehicleDetails.model && (
                            <option value="" disabled>No variants found</option>
                          )
                        )}
                      </select>
                      {missingFieldsModal.normalizedQuery?.variant && (
                        <span className="text-xs text-blue-600 flex items-center gap-1.5 font-medium">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Detected value: {missingFieldsModal.normalizedQuery.variant}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Fuel Type Field */}
                      {missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'fueltype' || f.toLowerCase() === 'fuel type') && (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        Fuel Type
                        <span className="text-red-500 text-xs font-bold">*</span>
                      </label>
                      <select
                        value={vehicleDetails.fuelType}
                        onChange={(e) => handleVehicleDetailChange('fuelType', e.target.value)}
                        disabled={isSection2Disabled || !vehicleDetails.model || (loadingVehicleData && vehicleFuelTypes.length === 0) || Boolean(vehicleDetailsFromNumber && vehicleDetails.fuelType)}
                        className="border-2 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        required
                        title={
                          vehicleDetailsFromNumber && vehicleDetails.fuelType 
                            ? "Disabled: Vehicle details fetched from vehicle number" 
                            : !vehicleDetails.model 
                            ? "Please select Model first"
                            : ""
                        }
                      >
                        <option value="">
                          {!vehicleDetails.model 
                            ? 'Select Fuel Type (choose Model first)' 
                            : (loadingVehicleData && vehicleFuelTypes.length === 0 ? 'Loading...' : 'Select Fuel Type')}
                        </option>
                        {vehicleFuelTypes.map((fuelType) => (
                          <option key={fuelType} value={fuelType}>{fuelType}</option>
                        ))}
                      </select>
                      {missingFieldsModal.normalizedQuery?.fuelType && (
                        <span className="text-xs text-blue-600 flex items-center gap-1.5 font-medium">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Detected value: {missingFieldsModal.normalizedQuery.fuelType}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Year Field - Dropdown populated from DB via lookup API */}
                      {missingFieldsModal.missingMMVFY.some(f => f.toLowerCase() === 'year') && (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        Year
                        <span className="text-red-500 text-xs font-bold">*</span>
                      </label>
                      <select
                        value={vehicleDetails.year}
                        onChange={(e) => handleVehicleDetailChange('year', e.target.value)}
                        disabled={isSection2Disabled || !vehicleDetails.model || (loadingYears && vehicleYears.length === 0) || Boolean(vehicleDetailsFromNumber && vehicleDetails.year)}
                        className="border-2 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed border-gray-300"
                        title={
                          vehicleDetailsFromNumber && vehicleDetails.year
                            ? "Disabled: Vehicle details fetched from vehicle number"
                            : !vehicleDetails.model
                            ? "Please select Model first"
                            : loadingYears
                            ? "Loading years..."
                            : vehicleYears.length === 0 && vehicleDetails.model
                            ? "Loading years for this model..."
                            : ""
                        }
                        required
                      >
                        <option value="">
                          {!vehicleDetails.model
                            ? 'Select Year (choose Model first)'
                            : loadingYears
                            ? 'Loading years...'
                            : vehicleYears.length === 0
                            ? 'Select Model to load years'
                            : `Select Year (${vehicleYears.length} available)`}
                        </option>
                        {vehicleYears.map((year) => (
                          <option key={year} value={String(year)}>{year}</option>
                        ))}
                      </select>
                      {yearError && (
                        <p className="text-xs text-red-600 mt-1 font-medium">{yearError}</p>
                      )}
                      {missingFieldsModal.normalizedQuery?.year && (
                        <span className="text-xs text-blue-600 flex items-center gap-1.5 font-medium">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Detected value: {missingFieldsModal.normalizedQuery.year}
                        </span>
                      )}
                    </div>
                  )}
                          </div>
                    </div>
                    );
                  })()}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={(() => {
                      const isPartInfoComplete = modalSelectedParts.length > 0 || partNumberInputValue.trim().length > 0 || selectedFiles.length > 0;
                      // Vehicle Details is complete if either all required MMVFY fields are filled OR vehicle number is provided
                      // Check if all required MMVFY fields are actually filled
                      // Variant is always required if any MMVFY field is missing
                      const variantRequired = missingFieldsModal.missingMMVFY.length > 0;
                      const variantFilled = !variantRequired || vehicleDetails.variant.trim().length > 0;
                      
                      // Check each missing MMVFY field
                      const allMissingFieldsFilled = missingFieldsModal.missingMMVFY.length === 0 || missingFieldsModal.missingMMVFY.every(field => {
                        const fieldLower = field.toLowerCase();
                        // If make is missing, both make and model are required (they go together)
                        if (fieldLower === 'make') {
                          return vehicleDetails.make.trim().length > 0 && vehicleDetails.model.trim().length > 0;
                        }
                        if (fieldLower === 'model') {
                          return vehicleDetails.model.trim().length > 0;
                        }
                        if (fieldLower === 'variant') {
                          return vehicleDetails.variant.trim().length > 0;
                        }
                        if (fieldLower === 'fueltype' || fieldLower === 'fuel type') {
                          return vehicleDetails.fuelType.trim().length > 0;
                        }
                        if (fieldLower === 'year') {
                          return vehicleDetails.year.trim().length > 0;
                        }
                        return true;
                      });
                      
                      const allRequiredMMVFYFilled = variantFilled && allMissingFieldsFilled;
                      const isVehicleInfoComplete = allRequiredMMVFYFilled || vehicleNumber.trim().length > 0;
                      return !(isPartInfoComplete && isVehicleInfoComplete);
                    })()}
                    className="px-6 py-2.5 text-sm font-semibold text-white bg-black hover:bg-gray-900 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-black"
                  >
                    <span>Continue Search</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Part Detection Modal */}
      {partDetectionModal.isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setPartDetectionModal(prev => ({ ...prev, isOpen: false }))}
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slideUp"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Part Detected</h3>
                  <p className="text-blue-100 text-sm mt-0.5">AI analysis of uploaded image</p>
                </div>
              </div>
              <button
                onClick={() => setPartDetectionModal(prev => ({ ...prev, isOpen: false }))}
                className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {partDetectionModal.isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-600 font-medium">Analyzing image...</p>
                  <p className="text-gray-500 text-sm mt-2">Detecting part information</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Image Preview */}
                  {partDetectionModal.imagePreview && (
                    <div className="flex justify-center">
                      <div className="relative rounded-xl overflow-hidden shadow-lg border-2 border-gray-200">
                        <img
                          src={partDetectionModal.imagePreview}
                          alt="Uploaded part"
                          className="w-full max-w-md h-auto object-contain"
                        />
                      </div>
                    </div>
                  )}

                  {/* Part Information */}
                  <div className="space-y-4">
                    {partDetectionModal.partName && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-1">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-blue-900 mb-1">Part Name</h4>
                            <p className="text-base font-bold text-blue-700">{partDetectionModal.partName}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {partDetectionModal.partDescription && (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Description</h4>
                        <p className="text-sm text-gray-700">{partDetectionModal.partDescription}</p>
                      </div>
                    )}

                    {(partDetectionModal.subAggregate || partDetectionModal.aggregate) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {partDetectionModal.subAggregate && (
                          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                            <h4 className="text-sm font-semibold text-green-800 mb-1">Sub Aggregate</h4>
                            <p className="text-sm font-medium text-green-700">{partDetectionModal.subAggregate}</p>
                          </div>
                        )}
                        {partDetectionModal.aggregate && (
                          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                            <h4 className="text-sm font-semibold text-purple-800 mb-1">Aggregate</h4>
                            <p className="text-sm font-medium text-purple-700">{partDetectionModal.aggregate}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-4 bg-gray-50">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setPartDetectionModal(prev => ({ ...prev, isOpen: false }))}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-xl transition-all duration-200"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setPartDetectionModal(prev => ({ ...prev, isOpen: false }));
                    // Trigger search with detected part information
                    handleSearch();
                  }}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  Continue to Search
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default HomePage;

